import { ComponentTypes, type ComponentType, type All8PageContent } from "@bcc/core-renderers";
import type { ComponentDefinition } from "./pageComponentLibrary.types";
import { DEFAULT_PRERENDER_IMAGE } from "./pageComponentLibrary.utils";

const HOME_AUDIENCE_ICONS = [
	{ label: "User", value: "User" },
	{ label: "Building2", value: "Building2" },
	{ label: "Megaphone", value: "Megaphone" },
	{ label: "Landmark", value: "Landmark" },
	{ label: "FlaskConical", value: "FlaskConical" },
	{ label: "GraduationCap", value: "GraduationCap" }
];

const ICON_CARD_ICONS = [
	{ label: "ShieldCheck", value: "ShieldCheck" },
	{ label: "Building2", value: "Building2" },
	{ label: "Banknote", value: "Banknote" },
	{ label: "Database", value: "Database" },
	{ label: "Target", value: "Target" },
	{ label: "Gauge", value: "Gauge" },
	{ label: "Zap", value: "Zap" }
];

const withPreview = (type: ComponentType, content: any): All8PageContent =>
	[
		{
			componentType: type as any,
			key: `preview-${type}`,
			content
		}
	] as All8PageContent;

// Array order IS the order the library shows, and `primary: true` is what puts a component in the
// « Essentiels » tier. The first three are deliberately the page's title hierarchy then its body:
// Titre principal (H1) → Sous-titre (H2) → Paragraphe.
export const componentLibrary: ComponentDefinition[] = [
	{
		type: ComponentTypes.PageHeader,
		category: "texte",
		primary: true,
		skeleton: { kind: "heading" },
		title: "Titre principal",
		description: "Titre principal de la page (un seul par page).",
		tags: ["intro", "header", "titre"],
		defaultContent: {
			title: "Page title"
		},
		fields: [
			{ type: "text", label: "Title", path: ["content", "title"] },
			{
				type: "select",
				label: "Gabarit",
				path: ["content", "variant"],
				options: [
					{ value: "", label: "Titre simple (défaut)" },
					{ value: "subject", label: "Bandeau de sujet (gabarit statistiques)" }
				],
				helper:
					"« Bandeau de sujet » reprend exactement le gabarit des pages /statistiques/{secteur}/{sujet} : bandeau blanc pleine largeur, fil d'Ariane, sur-titre orange, grand titre bleu et chapô. À combiner avec un fond de page « Bleu très clair » et une largeur « Fluide » dans les réglages de mise en page."
			},
			{
				type: "text",
				label: "Sur-titre",
				path: ["content", "eyebrow"],
				showWhen: { path: ["content", "variant"], equals: "subject" },
				helper: "Petite ligne orange au-dessus du titre."
			},
			{
				type: "textarea",
				label: "Chapô",
				path: ["content", "description"],
				showWhen: { path: ["content", "variant"], equals: "subject" },
				helper: "Paragraphe d'introduction sous le titre."
			},
			{
				type: "array",
				label: "Fil d'Ariane",
				path: ["content", "breadcrumb"],
				itemType: "object",
				itemLabel: "Étape",
				defaultItem: { label: "" },
				itemSummaryKeys: ["label"],
				showWhen: { path: ["content", "variant"], equals: "subject" },
				helper:
					"Chemin affiché au-dessus du titre. Laissez la dernière étape sans lien : elle marque la position courante.",
				fields: [
					{ type: "text", label: "Libellé", path: ["label"] },
					{ type: "href", label: "Lien", path: ["href"] }
				]
			}
		],
		previewComponents: withPreview(ComponentTypes.PageHeader, {
			title: "Page title"
		})
	},
	{
		type: ComponentTypes.SectionHeading,
		category: "texte",
		primary: true,
		skeleton: { kind: "heading" },
		title: "Sous-titre",
		description: "Sous-titre autonome pour séparer les sections.",
		tags: ["heading", "text", "titre"],
		defaultContent: {
			text: "Section heading",
			size: "large"
		},
		fields: [
			{ type: "text", label: "Text", path: ["content", "text"] },
			{
				type: "select",
				label: "Size",
				path: ["content", "size"],
				options: [
					{ label: "Large", value: "large" },
					{ label: "Medium", value: "medium" }
				]
			}
		],
		previewComponents: withPreview(ComponentTypes.SectionHeading, { text: "Section heading", size: "large" })
	},
	{
		type: ComponentTypes.TextBlock,
		category: "texte",
		primary: true,
		skeleton: { kind: "paragraph" },
		title: "Paragraphe",
		description: "Bloc de paragraphes pour le texte long.",
		tags: ["text", "content"],
		// ONE empty paragraph: the editor gets a single editor open on an empty field, ready to type.
		// (This seeded three placeholder paragraphs, so adding a paragraph meant deleting two and
		// clearing the sample text of the third.) `previewComponents` below keeps sample text : it only
		// feeds the library thumbnail, never the document.
		defaultContent: {
			paragraphs: [""]
		},
		fields: [
			{
				type: "array",
				label: "Paragraphes",
				itemLabel: "Paragraphe",
				itemType: "text",
				defaultItem: "",
				path: ["content", "paragraphs"],
				itemEditor: "richParagraph"
			}
		],
		previewComponents: withPreview(ComponentTypes.TextBlock, {
			paragraphs: [
				"Paragraph 1",
				"Paragraph 2",
				"Paragraph 3"
			]
		})
	},
	{
		type: ComponentTypes.DynamicComponents,
		category: "donnees",
		skeleton: { kind: "columns", spans: [12] },
		title: "Composants dynamiques",
		description: "Injecte des composants depuis un champ de contenu dynamique (modèles uniquement).",
		tags: ["dynamic", "template", "components"],
		defaultContent: {
			variableKey: "components"
		},
		fields: [
			{
				type: "text",
				label: "Variable key",
				path: ["content", "variableKey"],
				placeholder: "components",
				helper: "Matches the field key on the selected dynamic content type."
			}
		],
		previewComponents: withPreview(ComponentTypes.DynamicComponents, {
			variableKey: "components"
		})
	},
	{
		type: ComponentTypes.AccordionSection,
		category: "texte",
		skeleton: { kind: "list", rows: 3 },
		title: "Accordéon",
		description: "Éléments dépliables avec titre et paragraphes enrichis.",
		tags: ["accordion", "faq", "content"],
		defaultContent: {
			items: [
				{ title: "Quel est le meilleur moment pour nous rendre visite", paragraphs: ["Réponse ou contenu de la première section."] },
				{ title: "Préparez votre visite", paragraphs: ["Contenu pour préparer la visite."] },
				{ title: "Itinéraire pour nous rejoindre", paragraphs: ["Instructions et itinéraire."] }
			]
		},
		fields: [
			{
				type: "array",
				label: "Items",
				itemLabel: "Item",
				itemType: "object",
				defaultItem: { title: "", paragraphs: [""] },
				path: ["content", "items"],
				fields: [
					{ type: "text", label: "Title", path: ["title"], placeholder: "Titre de l’item" },
					{
						type: "array",
						label: "Paragraphes",
						itemLabel: "Paragraphe",
						itemType: "text",
						defaultItem: "",
						path: ["paragraphs"],
						itemEditor: "richParagraph"
					}
				]
			}
		],
		previewComponents: withPreview(ComponentTypes.AccordionSection, {
			items: [
				{ title: "Quel est le meilleur moment pour nous rendre visite", paragraphs: ["Réponse ou contenu."] },
				{ title: "Préparez votre visite", paragraphs: ["Contenu."] },
				{ title: "Itinéraire pour nous rejoindre", paragraphs: ["Instructions."] }
			]
		})
	},
	{
		type: ComponentTypes.Coordonnees,
		category: "texte",
		skeleton: { kind: "columns", spans: [5, 7] },
		title: "Coordonnées",
		description: "Mise en page en deux colonnes : image à gauche, coordonnées à droite.",
		tags: ["contact", "coordonnées", "image"],
		defaultContent: {
			imageSrc: DEFAULT_PRERENDER_IMAGE,
			imageAlt: "Image",
			topTitle: "Les coordonnées générales",
			introParagraph: "Pour toute demande générale, vous pouvez contacter la Banque Centrale du Congo via les coordonnées ci-dessous :",
			phoneHref: { external: "tel:+243123456789" },
			emailHref: { external: "mailto:contact@bcc.cd" },
			secondTitle: "Adresse et horaires",
			addressLinkText: "563 Boulevard Colonel Tshatshi - Gombé - Kinshasa",
			addressLinkHref: { external: "https://maps.example.com" },
			addressLines: ["Boîte Postale : 2697 Kinshasa 1 République Démocratique du Congo"]
		},
		fields: [
			{ type: "image", label: "Image", path: ["content", "imageSrc"], helper: "Image gauche." },
			{ type: "text", label: "Image alt", path: ["content", "imageAlt"] },
			{ type: "text", label: "Titre principal", path: ["content", "topTitle"], placeholder: "Les coordonnées générales" },
			{ type: "textarea", label: "Paragraphe d’intro", path: ["content", "introParagraph"] },
			{ type: "text", label: "Lien téléphone (ex: tel:+243...)", path: ["content", "phoneHref", "external"], placeholder: "tel:+243..." },
			{ type: "text", label: "Lien email (ex: mailto:...)", path: ["content", "emailHref", "external"], placeholder: "mailto:contact@..." },
			{ type: "text", label: "Titre adresse", path: ["content", "secondTitle"], placeholder: "Adresse et horaires" },
			{ type: "text", label: "Adresse (lien, texte)", path: ["content", "addressLinkText"], placeholder: "563 Boulevard..." },
			{ type: "text", label: "Adresse (lien, URL)", path: ["content", "addressLinkHref", "external"], placeholder: "https://..." },
			{
				type: "array",
				label: "Lignes adresse (supplément)",
				itemLabel: "Ligne",
				itemType: "text",
				defaultItem: "",
				path: ["content", "addressLines"]
			}
		],
		previewComponents: withPreview(ComponentTypes.Coordonnees, {
			imageSrc: DEFAULT_PRERENDER_IMAGE,
			imageAlt: "Image",
			topTitle: "Les coordonnées générales",
			introParagraph: "Pour toute demande générale, vous pouvez contacter la Banque Centrale du Congo via les coordonnées ci-dessous :",
			phoneHref: { external: "tel:+243123456789" },
			emailHref: { external: "mailto:contact@bcc.cd" },
			secondTitle: "Adresse et horaires",
			addressLinkText: "563 Boulevard Colonel Tshatshi - Gombé - Kinshasa",
			addressLinkHref: { external: "#" },
			addressLines: ["Boîte Postale : 2697 Kinshasa 1 République Démocratique du Congo"]
		})
	},
	{
		type: ComponentTypes.SeeAlso,
		category: "cartes",
		skeleton: { kind: "cards" },
		title: "Voir aussi",
		description: "Panneau de renvois en fin de page : sur-titre, titre et deux à quatre destinations liées.",
		tags: ["liens", "renvoi", "voir aussi", "source"],
		defaultContent: {
			eyebrow: "Source en direct",
			title: "Voir aussi",
			text: "",
			items: [{ title: "", text: "" }]
		},
		fields: [
			{
				type: "text",
				label: "Sur-titre",
				path: ["content", "eyebrow"],
				placeholder: "Source en direct",
				helper: "Petite ligne orange au-dessus du titre. Vide, elle affiche « Source en direct »."
			},
			{
				type: "text",
				label: "Titre",
				path: ["content", "title"],
				placeholder: "Voir aussi"
			},
			{
				type: "textarea",
				label: "Chapô",
				path: ["content", "text"],
				helper: "Une phrase pour dire ce que le visiteur trouvera sur les pages liées. Facultatif."
			},
			{
				type: "array",
				label: "Destinations",
				path: ["content", "items"],
				itemType: "object",
				itemLabel: "Destination",
				defaultItem: { title: "", text: "" },
				itemSummaryKeys: ["title"],
				fields: [
					{ type: "text", label: "Titre", path: ["title"] },
					{ type: "textarea", label: "Description", path: ["text"] },
					{ type: "href", label: "Lien", path: ["href"] }
				]
			}
		],
		previewComponents: withPreview(ComponentTypes.SeeAlso, {
			eyebrow: "Source en direct",
			title: "Voir aussi",
			text: "Cette page publie la série historique auditée.",
			items: [
				{ title: "Cours de change du jour", text: "Cours officiels du jour, mis à jour depuis le flux de la Banque Centrale." },
				{ title: "Convertisseur de devises", text: "Convertir un montant au cours du jour." }
			]
		})
	},
	{
		type: ComponentTypes.BlueCallout,
		category: "texte",
		skeleton: { kind: "cta" },
		title: "Encadré bleu",
		description: "Deux colonnes : panneau bleu à gauche avec titre blanc, paragraphes et image à droite.",
		tags: ["callout", "image", "blue"],
		defaultContent: {
			title: "Stabilité et dynamisme : la stratégie monétaire de la BCC",
			text: "Pendant longtemps, le taux directeur à 25% a freiné l'inflation et stabilisé la monnaie. Les réserves obligatoires à 12-13% régulent la liquidité bancaire, tandis que les réserves de change couvrent 3 mois d'importations.",
			imageSrc: DEFAULT_PRERENDER_IMAGE,
			imageAlt: "Image"
		},
		fields: [
			{ type: "text", label: "Title", path: ["content", "title"] },
			{ type: "textarea", label: "Text", path: ["content", "text"] },
			{ type: "image", label: "Image (right)", path: ["content", "imageSrc"], helper: "Image displayed on the right." },
			{ type: "text", label: "Image alt", path: ["content", "imageAlt"] },
			{ type: "text", label: "Link slug (optional)", path: ["content", "ctaHref", "slug"], placeholder: "/path" },
			{ type: "text", label: "Link external URL (optional)", path: ["content", "ctaHref", "external"], placeholder: "https://..." }
		],
		previewComponents: withPreview(ComponentTypes.BlueCallout, {
			title: "Stabilité et dynamisme : la stratégie monétaire de la BCC",
			text: "Pendant longtemps, le taux directeur à 25% a freiné l'inflation et stabilisé la monnaie.",
			imageSrc: DEFAULT_PRERENDER_IMAGE,
			imageAlt: "Image"
		})
	},
	{
		type: ComponentTypes.Margin,
		category: "mise-en-page",
		skeleton: { kind: "columns", spans: [12] },
		title: "Espaceur",
		description: "Ajoute un espace vertical entre les sections.",
		tags: ["layout", "spacing"],
		defaultContent: {
			mobile: "mt-8",
			md: "md:mt-10",
			lg: "lg:mt-12"
		},
		fields: [
			{
				type: "text",
				label: "Mobile spacing",
				path: ["content", "mobile"],
				placeholder: "mt-8",
				helper: "Tailwind margin-top class for mobile."
			},
			{
				type: "text",
				label: "MD spacing",
				path: ["content", "md"],
				placeholder: "md:mt-10",
				helper: "Tailwind margin-top class for tablet (md)."
			},
			{
				type: "text",
				label: "LG spacing",
				path: ["content", "lg"],
				placeholder: "lg:mt-12",
				helper: "Tailwind margin-top class for desktop (lg)."
			}
		],
		previewComponents: [
			{
				componentType: ComponentTypes.SectionHeading,
				key: "preview-margin-top",
				content: { text: "Section A", size: "medium" }
			},
			{
				componentType: ComponentTypes.Margin,
				key: "preview-margin",
				content: { mobile: "mt-8", md: "md:mt-10", lg: "lg:mt-12" }
			},
			{
				componentType: ComponentTypes.SectionHeading,
				key: "preview-margin-bottom",
				content: { text: "Section B", size: "medium" }
			}
		] as All8PageContent
	},
	{
		type: ComponentTypes.Hero,
		category: "accueil",
		skeleton: { kind: "hero" },
		title: "Bannière de section",
		description: "Bannière institutionnelle avec mise en page scindée ou image de fond historique.",
		tags: ["landing", "home", "section"],
		defaultContent: {
			variant: "texture",
			eyebrow: "BANQUE CENTRALE DU CONGO",
			title: "Hero title",
			subtitle: "Hero subtitle",
			body: "Hero body text",
			backgroundImageSrc: DEFAULT_PRERENDER_IMAGE,
			imageAlt: ""
		},
		fields: [
			{
				type: "select",
				label: "Disposition",
				path: ["content", "variant"],
				options: [
					{ label: "Bannière claire texturée", value: "texture" },
					{ label: "Section scindée", value: "split" },
					{ label: "Image de fond historique", value: "overlay" }
				]
			},
			{ type: "text", label: "Sur-titre", path: ["content", "eyebrow"] },
			{ type: "text", label: "Title", path: ["content", "title"] },
			{ type: "text", label: "Subtitle", path: ["content", "subtitle"] },
			{ type: "textarea", label: "Body", path: ["content", "body"] },
			{
				type: "image",
				label: "Background image",
				path: ["content", "backgroundImageSrc"],
				helper: "Select an image from storage (saved as a CDN key without the domain)."
			},
			{ type: "text", label: "Texte alternatif de l'image", path: ["content", "imageAlt"], showWhen: { path: ["content", "variant"], equals: "split" } }
		],
		previewComponents: withPreview(ComponentTypes.Hero, {
			variant: "texture",
			eyebrow: "BANQUE CENTRALE DU CONGO",
			title: "Hero title",
			subtitle: "Hero subtitle",
			body: "Hero body text",
			backgroundImageSrc: DEFAULT_PRERENDER_IMAGE,
			imageAlt: ""
		})
	},
	{
		type: ComponentTypes.NewsSection,
		category: "accueil",
		skeleton: { kind: "cards", count: 3 },
		title: "Section actualités",
		description: "Article a la une accompagné de titres secondaires.",
		tags: ["news", "home", "dynamic"],
		defaultContent: {
			mode: "manual",
			dynamicComponent: {
				contentTypeKey: "",
				dynamicPageId: "",
				sortField: "",
				featuredFilter: [],
				itemsFilter: [],
				featuredCount: 1,
				itemsCount: 3,
				fieldMapping: {
					title: "",
					excerpt: "",
					image: "",
					href: ""
				}
			},
			featured: {
				id: "feature-1",
				title: "Featured title",
				excerpt: "Featured excerpt",
				image: DEFAULT_PRERENDER_IMAGE,
				href: { slug: "/news/featured" }
			},
			items: [
				{
					id: "news-1",
					title: "News title 1",
					excerpt: "News excerpt 1",
					image: DEFAULT_PRERENDER_IMAGE,
					href: { slug: "/news/item-1" }
				},
				{
					id: "news-2",
					title: "News title 2",
					excerpt: "News excerpt 2",
					image: DEFAULT_PRERENDER_IMAGE,
					href: { slug: "/news/item-2" }
				}
			]
		},
		fields: [
			{
				type: "select",
				label: "Mode",
				path: ["content", "mode"],
				options: [
					{ label: "Manual", value: "manual" },
					{ label: "Dynamic", value: "dynamic" }
				],
				helper: "Manual uses the content below. Dynamic pulls items from a content type."
			},
			{
				type: "object",
				label: "Dynamic settings",
				path: ["content", "dynamicComponent"],
				showWhen: { path: ["content", "mode"], equals: "dynamic" },
				fields: [
					{
						type: "contentTypeSelect",
						label: "Content type",
						path: ["contentTypeKey"],
						helper: "Select the content type to pull items from."
					},
					{
						type: "dynamicPageSelect",
						label: "Dynamic page",
						path: ["dynamicPageId"],
						contentTypePath: ["content", "dynamicComponent", "contentTypeKey"],
						allowEmpty: true,
						helper: "Pick the dynamic page template to link to (same content type)."
					},
					{
						type: "contentFieldSelect",
						label: "Sort field (date)",
						path: ["sortField"],
						contentTypePath: ["content", "dynamicComponent", "contentTypeKey"],
						fieldTypes: ["date"],
						includeBaseFields: false,
						allowEmpty: true,
						valueFormat: "raw",
						helper: "Used for both featured and items (latest first)."
					},
					{
						type: "number",
						label: "Featured count",
						path: ["featuredCount"],
						helper: "Number of candidates for the featured slot."
					},
					{
						type: "number",
						label: "Items count",
						path: ["itemsCount"],
						helper: "Number of items to show in the list."
					},
					{
						type: "array",
						label: "Featured filters",
						itemLabel: "Filter",
						itemType: "object",
						defaultItem: { field: "", value: "" },
						path: ["featuredFilter"],
						fields: [
							{
								type: "contentFieldSelect",
								label: "Field",
								path: ["field"],
								contentTypePath: ["content", "dynamicComponent", "contentTypeKey"],
								includeBaseFields: true,
								allowEmpty: true,
								valueFormat: "raw"
							},
							{
								type: "contentFieldValue",
								label: "Value",
								path: ["value"],
								contentTypePath: ["content", "dynamicComponent", "contentTypeKey"],
								fieldPath: ["field"]
							}
						]
					},
					{
						type: "array",
						label: "Items filters",
						itemLabel: "Filter",
						itemType: "object",
						defaultItem: { field: "", value: "" },
						path: ["itemsFilter"],
						fields: [
							{
								type: "contentFieldSelect",
								label: "Field",
								path: ["field"],
								contentTypePath: ["content", "dynamicComponent", "contentTypeKey"],
								includeBaseFields: true,
								allowEmpty: true,
								valueFormat: "raw"
							},
							{
								type: "contentFieldValue",
								label: "Value",
								path: ["value"],
								contentTypePath: ["content", "dynamicComponent", "contentTypeKey"],
								fieldPath: ["field"]
							}
						]
					},
					{
						type: "object",
						label: "Field mapping",
						path: ["fieldMapping"],
						fields: [
							{
								type: "contentFieldSelect",
								label: "Title field",
								path: ["title"],
								contentTypePath: ["content", "dynamicComponent", "contentTypeKey"],
								fieldTypes: ["text"],
								includeBaseFields: true,
								valueFormat: "raw",
								helper: "Select the field to use for the title."
							},
							{
								type: "contentFieldSelect",
								label: "Description field",
								path: ["excerpt"],
								contentTypePath: ["content", "dynamicComponent", "contentTypeKey"],
								fieldTypes: ["text"],
								includeBaseFields: true,
								valueFormat: "raw",
								helper: "Select the field to use for the excerpt."
							},
							{
								type: "contentFieldSelect",
								label: "Image field",
								path: ["image"],
								contentTypePath: ["content", "dynamicComponent", "contentTypeKey"],
								fieldTypes: ["image"],
								includeBaseFields: false,
								valueFormat: "raw",
								helper: "Select the image field to use."
							},
							{
								type: "contentFieldSelect",
								label: "Link field",
								path: ["href"],
								contentTypePath: ["content", "dynamicComponent", "contentTypeKey"],
								fieldTypes: ["text", "url"],
								includeBaseFields: true,
								valueFormat: "raw",
								helper: "Select the field used for the link (slug/path/url)."
							}
						]
					}
				]
			},
			{
				type: "object",
				label: "Featured story",
				path: ["content", "featured"],
				showWhen: { path: ["content", "mode"], equals: "manual" },
				fields: [
					{ type: "text", label: "Title", path: ["title"] },
					{ type: "textarea", label: "Excerpt", path: ["excerpt"] },
					{
						type: "image",
						label: "Image",
						path: ["image"],
						helper: "Pick an image from storage (CDN key only)."
					},
					{
						type: "href",
						label: "Destination",
						path: ["href"],
						helper: "Choose an existing page or an external URL."
					}
				]
			},
			{
				type: "array",
				label: "News items",
				itemLabel: "News",
				itemType: "object",
				defaultItem: { title: "", excerpt: "", image: DEFAULT_PRERENDER_IMAGE, href: { slug: "" } },
				path: ["content", "items"],
				showWhen: { path: ["content", "mode"], equals: "manual" },
				fields: [
					{ type: "text", label: "Title", path: ["title"] },
					{ type: "textarea", label: "Excerpt", path: ["excerpt"] },
					{
						type: "image",
						label: "Image",
						path: ["image"],
						helper: "Pick an image from storage (CDN key only)."
					},
					{
						type: "href",
						label: "Destination",
						path: ["href"],
						helper: "Select a site page or paste an external URL."
					}
				]
			}
		],
		previewComponents: withPreview(ComponentTypes.NewsSection, {
			mode: "manual",
			dynamicComponent: {
				contentTypeKey: "",
				sortField: "",
				featuredFilter: [],
				itemsFilter: [],
				featuredCount: 1,
				itemsCount: 3,
				fieldMapping: {
					title: "",
					excerpt: "",
					image: "",
					href: ""
				}
			},
			featured: {
				id: "feature-1",
				title: "Featured title",
				excerpt: "Featured excerpt",
				image: DEFAULT_PRERENDER_IMAGE,
				href: { slug: "/news/featured" }
			},
			items: [
				{
					id: "news-1",
					title: "News title 1",
					excerpt: "News excerpt 1",
					image: DEFAULT_PRERENDER_IMAGE,
					href: { slug: "/news/item-1" }
				},
				{
					id: "news-2",
					title: "News title 2",
					excerpt: "News excerpt 2",
					image: DEFAULT_PRERENDER_IMAGE,
					href: { slug: "/news/item-2" }
				}
			]
		})
	},
	{
		type: ComponentTypes.NewsList,
		category: "accueil",
		skeleton: { kind: "list", rows: 3 },
		title: "Liste d'actualités",
		description: "Liste verticale d'actualités avec panneaux gris facultatifs.",
		tags: ["news", "list"],
		defaultContent: {
			useGrayPanel: true,
			items: [
				{
					tag: "TAG 1",
					title: "News title 1",
					excerpt: "News excerpt 1",
					date: "01/2025",
					href: { slug: "/news/item-1" }
				},
				{
					tag: "TAG 2",
					title: "News title 2",
					meta: "Meta line",
					date: "02/2025",
					href: { slug: "/news/item-2" }
				}
			],
			ctaLabel: "All news",
			ctaHref: { slug: "/news" }
		},
		fields: [
			{
				type: "select",
				label: "Gray panel background",
				path: ["content", "useGrayPanel"],
				options: [
					{ label: "Yes (use #F5F5F5 panel)", value: "true" },
					{ label: "No (transparent)", value: "false" }
				]
			},
			{
				type: "array",
				label: "News items",
				itemLabel: "News",
				itemType: "object",
				defaultItem: { tag: "", title: "", excerpt: "", meta: "", date: "", href: { slug: "" } },
				path: ["content", "items"],
				fields: [
					{ type: "text", label: "Tag", path: ["tag"] },
					{ type: "text", label: "Title", path: ["title"] },
					{ type: "textarea", label: "Excerpt", path: ["excerpt"] },
					{ type: "text", label: "Meta line", path: ["meta"], helper: "Optional subtitle (location, timeframe, etc.)." },
					{ type: "text", label: "Date", path: ["date"], placeholder: "DD/MM/YYYY" },
					{
						type: "href",
						label: "Destination",
						path: ["href"],
						helper: "Select a page or paste an external URL."
					}
				]
			},
			{ type: "text", label: "CTA label", path: ["content", "ctaLabel"] },
			{
				type: "href",
				label: "CTA destination",
				path: ["content", "ctaHref"],
				helper: "Link to the news listing page."
			}
		],
		previewComponents: withPreview(ComponentTypes.NewsList, {
			useGrayPanel: true,
			items: [
				{
					tag: "TAG 1",
					title: "News title 1",
					excerpt: "News excerpt 1",
					date: "01/2025",
					href: { slug: "/news/item-1" }
				},
				{
					tag: "TAG 2",
					title: "News title 2",
					meta: "Meta line",
					date: "02/2025",
					href: { slug: "/news/item-2" }
				}
			],
			ctaLabel: "All news",
			ctaHref: { slug: "/news" }
		})
	},
	{
		type: ComponentTypes.StatsSummary,
		category: "donnees",
		skeleton: { kind: "chart" },
		title: "Synthèse statistique",
		description: "Taux clés et enquêtes en un coup d'œil.",
		tags: ["stats", "home"],
		defaultContent: {
			interestRates: [
				{ label: "Rate 1", value: "0.00%" },
				{ label: "Rate 2", value: "0.00%" }
			],
			exchangeRates: [
				{ code: "CUR", value: "0.00" },
				{ code: "CUR", value: "0.00" }
			],
			surveys: [
				{ label: "Survey 1", value: "0" },
				{ label: "Survey 2", value: "0" }
			]
		},
		fields: [
			{
				type: "array",
				label: "Interest rates",
				itemLabel: "Rate",
				itemType: "object",
				defaultItem: { label: "", value: "" },
				path: ["content", "interestRates"],
				fields: [
					{ type: "text", label: "Label", path: ["label"] },
					{ type: "text", label: "Value", path: ["value"], placeholder: "0.00%" }
				]
			},
			{
				type: "array",
				label: "Exchange rates",
				itemLabel: "Rate",
				itemType: "object",
				defaultItem: { code: "", value: "" },
				path: ["content", "exchangeRates"],
				fields: [
					{ type: "text", label: "Code", path: ["code"], placeholder: "CUR" },
					{ type: "text", label: "Value", path: ["value"], placeholder: "0.00" }
				]
			},
			{
				type: "array",
				label: "Surveys",
				itemLabel: "Survey",
				itemType: "object",
				defaultItem: { label: "", value: "" },
				path: ["content", "surveys"],
				fields: [
					{ type: "text", label: "Label", path: ["label"] },
					{ type: "text", label: "Value", path: ["value"] }
				]
			}
		],
		previewComponents: withPreview(ComponentTypes.StatsSummary, {
			interestRates: [
				{ label: "Rate 1", value: "0.00%" },
				{ label: "Rate 2", value: "0.00%" }
			],
			exchangeRates: [
				{ code: "CUR", value: "0.00" },
				{ code: "CUR", value: "0.00" }
			],
			surveys: [
				{ label: "Survey 1", value: "0" },
				{ label: "Survey 2", value: "0" }
			]
		})
	},
	{
		type: ComponentTypes.Callout,
		category: "texte",
		skeleton: { kind: "cta" },
		title: "Encadré",
		description: "Encadré compact d'appel à l'action avec un lien.",
		tags: ["cta", "highlight"],
		defaultContent: {
			title: "Callout title",
			linkLabel: "Callout link",
			linkHref: { slug: "/path" }
		},
		fields: [
			{ type: "text", label: "Title", path: ["content", "title"] },
			{ type: "text", label: "Link label", path: ["content", "linkLabel"] },
			{
				type: "href",
				label: "Link destination",
				path: ["content", "linkHref"],
				helper: "Point to a page, manual path, or external URL."
			}
		],
		previewComponents: withPreview(ComponentTypes.Callout, {
			title: "Callout title",
			linkLabel: "Callout link",
			linkHref: "#"
		})
	},
	{
		type: ComponentTypes.CtaBanner,
		category: "texte",
		skeleton: { kind: "cta" },
		title: "Bannière d'appel à l'action",
		description: "Bannière avec titre et lien d'action.",
		tags: ["cta", "banner"],
		defaultContent: {
			title: "Banner title",
			linkLabel: "Learn more",
			linkHref: { slug: "/link" }
		},
		fields: [
			{ type: "text", label: "Title", path: ["content", "title"] },
			{ type: "text", label: "Link label", path: ["content", "linkLabel"] },
			{
				type: "href",
				label: "Link destination",
				path: ["content", "linkHref"],
				helper: "Select a page or paste an external URL."
			}
		],
		previewComponents: withPreview(ComponentTypes.CtaBanner, {
			title: "Banner title",
			linkLabel: "Learn more",
			linkHref: { slug: "/link" }
		})
	},
	{
		type: ComponentTypes.LinkTileGrid,
		category: "cartes",
		skeleton: { kind: "cards", count: 3 },
		title: "Grille de tuiles de liens",
		description: "Grille de tuiles de liens simples.",
		tags: ["links", "grid"],
		defaultContent: {
			items: [
				{ label: "Link 1", href: { slug: "/link-1" } },
				{ label: "Link 2", href: { slug: "/link-2" } },
				{ label: "Link 3", href: { slug: "/link-3" } },
				{ label: "Link 4", href: { slug: "/link-4" } }
			]
		},
		fields: [
			{
				type: "array",
				label: "Links",
				itemLabel: "Link",
				itemType: "object",
				defaultItem: { label: "", href: { slug: "" } },
				path: ["content", "items"],
				fields: [
					{ type: "text", label: "Label", path: ["label"] },
					{
						type: "href",
						label: "Destination",
						path: ["href"],
						helper: "Pick an internal page or external link for this tile."
					}
				]
			}
		],
		previewComponents: withPreview(ComponentTypes.LinkTileGrid, {
			items: [
				{ label: "Link 1", href: { slug: "/link-1" } },
				{ label: "Link 2", href: { slug: "/link-2" } },
				{ label: "Link 3", href: { slug: "/link-3" } },
				{ label: "Link 4", href: { slug: "/link-4" } }
			]
		})
	},
	{
		type: ComponentTypes.AudienceTools,
		category: "accueil",
		skeleton: { kind: "cards", count: 4 },
		title: "Outils par public",
		description: "Points d'entrée rapides par segment de public.",
		tags: ["home", "links"],
		defaultContent: {
			searchPlaceholder: "Search",
			audiences: [
				{ icon: "User", label: "Audience 1", href: { slug: "/audience-1" } },
				{ icon: "Building2", label: "Audience 2", href: { slug: "/audience-2" } },
				{ icon: "Megaphone", label: "Audience 3", href: { slug: "/audience-3" } }
			]
		},
		fields: [
			{ type: "text", label: "Search placeholder", path: ["content", "searchPlaceholder"] },
			{
				type: "array",
				label: "Audiences",
				itemLabel: "Audience",
				itemType: "object",
				defaultItem: { icon: "User", label: "", href: { slug: "" } },
				path: ["content", "audiences"],
				fields: [
					{ type: "select", label: "Icon", path: ["icon"], options: HOME_AUDIENCE_ICONS },
					{ type: "text", label: "Label", path: ["label"] },
					{
						type: "href",
						label: "Destination",
						path: ["href"],
						helper: "Internal audience page or external URL."
					}
				]
			}
		],
		previewComponents: withPreview(ComponentTypes.AudienceTools, {
			searchPlaceholder: "Search",
			audiences: [
				{ icon: "User", label: "Audience 1", href: { slug: "/audience-1" } },
				{ icon: "Building2", label: "Audience 2", href: { slug: "/audience-2" } },
				{ icon: "Megaphone", label: "Audience 3", href: { slug: "/audience-3" } }
			]
		})
	},
	{
		type: ComponentTypes.SearchBar,
		category: "formulaires",
		skeleton: { kind: "form" },
		title: "Barre de recherche",
		description: "Bandeau bleu clair avec titre et champ de recherche centré.",
		tags: ["home", "search"],
		defaultContent: {
			title: "Search",
			searchPlaceholder: "Search",
			bold: false
		},
		fields: [
			{ type: "text", label: "Title", path: ["content", "title"], placeholder: "Search title" },
			{ type: "text", label: "Search placeholder", path: ["content", "searchPlaceholder"], placeholder: "Search" },
			{
				type: "select",
				label: "Bold style",
				path: ["content", "bold"],
				options: [
					{ label: "No (light blue bar)", value: "false" },
					{ label: "Yes (blue #005CA9, white text)", value: "true" }
				]
			}
		],
		previewComponents: withPreview(ComponentTypes.SearchBar, {
			title: "Search",
			searchPlaceholder: "Search",
			bold: false
		})
	},
	{
		type: ComponentTypes.PublicationsUpdates,
		category: "accueil",
		skeleton: { kind: "columns", spans: [6, 6] },
		title: "Publications + mises à jour",
		description: "Double colonne de publications et de mises à jour statistiques.",
		tags: ["home", "content"],
		defaultContent: {
			backgroundImageSrc: DEFAULT_PRERENDER_IMAGE,
			publications: [
				{
					id: "pub-1",
					tag: "Tag 1",
					date: "01/2025",
					title: "Publication title 1",
					href: { slug: "/publications/item-1" }
				},
				{
					id: "pub-2",
					tag: "Tag 2",
					date: "02/2025",
					title: "Publication title 2",
					href: { slug: "/publications/item-2" }
				}
			],
			statUpdates: [
				{
					category: "Category",
					period: "2025-01",
					items: [
						{ title: "Update 1" },
						{ title: "Update 2", subtitle: "" }
					]
				}
			]
		},
		fields: [
			{
				type: "image",
				label: "Background image",
				path: ["content", "backgroundImageSrc"],
				helper: "Select a background image from storage (CDN key only)."
			},
			{
				type: "array",
				label: "Publications",
				itemLabel: "Publication",
				itemType: "object",
				defaultItem: { id: "", tag: "", date: "", title: "", href: { slug: "" } },
				path: ["content", "publications"],
				fields: [
					{ type: "text", label: "Id", path: ["id"] },
					{ type: "text", label: "Tag", path: ["tag"] },
					{ type: "text", label: "Date", path: ["date"], placeholder: "MM/YYYY" },
					{ type: "text", label: "Title", path: ["title"] },
					{
						type: "href",
						label: "Destination",
						path: ["href"],
						helper: "Select a publication page or paste an external link."
					}
				]
			},
			{
				type: "array",
				label: "Stat updates",
				itemLabel: "Category",
				itemType: "object",
				defaultItem: { category: "", period: "", items: [] },
				path: ["content", "statUpdates"],
				fields: [
					{ type: "text", label: "Category", path: ["category"] },
					{ type: "text", label: "Period", path: ["period"], placeholder: "YYYY-MM" },
					{
						type: "array",
						label: "Items",
						itemLabel: "Entry",
						itemType: "object",
						defaultItem: { title: "", subtitle: "" },
						path: ["items"],
						fields: [
							{ type: "text", label: "Title", path: ["title"] },
							{ type: "text", label: "Subtitle", path: ["subtitle"], placeholder: "Optional" }
						]
					}
				]
			}
		],
		previewComponents: withPreview(ComponentTypes.PublicationsUpdates, {
			backgroundImageSrc: DEFAULT_PRERENDER_IMAGE,
			publications: [
				{
					id: "pub-1",
					tag: "Tag 1",
					date: "01/2025",
					title: "Publication title 1",
					href: { slug: "/publications/item-1" }
				},
				{
					id: "pub-2",
					tag: "Tag 2",
					date: "02/2025",
					title: "Publication title 2",
					href: { slug: "/publications/item-2" }
				}
			],
			statUpdates: [
				{
					category: "Category",
					period: "2025-01",
					items: [
						{ title: "Update 1" },
						{ title: "Update 2", subtitle: "" }
					]
				}
			]
		})
	},
	{
		type: ComponentTypes.ActivityGrid,
		category: "cartes",
		skeleton: { kind: "cards", count: 6, withImage: true },
		title: "Grille d'activités",
		description: "Grille de six cartes issues de la section Activités.",
		tags: ["grid", "activities"],
		defaultContent: {
			items: [
				{
					title: "Activity 1",
					description: "Activity description 1",
					href: { slug: "/activity-1" },
					imageSrc: DEFAULT_PRERENDER_IMAGE
				},
				{
					title: "Activity 2",
					description: "Activity description 2",
					href: { slug: "/activity-2" },
					imageSrc: DEFAULT_PRERENDER_IMAGE
				}
			]
		},
		fields: [
			{
				type: "array",
				label: "Activity items",
				itemLabel: "Activity",
				itemType: "object",
				defaultItem: { title: "", description: "", href: { slug: "" }, imageSrc: DEFAULT_PRERENDER_IMAGE },
				path: ["content", "items"],
				fields: [
					{ type: "text", label: "Title", path: ["title"] },
					{ type: "textarea", label: "Description", path: ["description"] },
					{
						type: "href",
						label: "Destination",
						path: ["href"],
						helper: "Choose an activity page or external destination."
					},
					{
						type: "image",
						label: "Image src",
						path: ["imageSrc"],
						helper: "Select an image from storage (CDN key only)."
					}
				]
			}
		],
		previewComponents: withPreview(ComponentTypes.ActivityGrid, {
			items: [
				{
					title: "Activity 1",
					description: "Activity description 1",
					href: { slug: "/activity-1" },
					imageSrc: DEFAULT_PRERENDER_IMAGE
				},
				{
					title: "Activity 2",
					description: "Activity description 2",
					href: { slug: "/activity-2" },
					imageSrc: DEFAULT_PRERENDER_IMAGE
				}
			]
		})
	},
	{
		type: ComponentTypes.PaginatedActivityGrid,
		category: "cartes",
		skeleton: { kind: "cards", count: 6, withImage: true },
		title: "Grille d'activités paginée",
		description: "Grille d'activités dynamique avec recherche, filtres et pagination.",
		tags: ["grid", "activities", "dynamic", "pagination", "card"],
		defaultContent: {
			mode: "dynamic",
			dynamicComponent: {
				contentTypeKey: "",
				dynamicPageId: "",
				sortField: "",
				filters: [],
				itemsPerPage: 24,
				enableTaxonomyFilter: "true",
				enableCategoryFilter: "true",
				searchPlaceholder: "Rechercher",
				cardType: "ActivityGrid",
				fieldMapping: {
					title: "",
					description: "",
					image: "",
					imageAlt: "",
					href: ""
				}
			},
			items: [
				{
					title: "Activity 1",
					description: "Activity description 1",
					href: { slug: "/activity-1" },
					imageSrc: DEFAULT_PRERENDER_IMAGE
				},
				{
					title: "Activity 2",
					description: "Activity description 2",
					href: { slug: "/activity-2" },
					imageSrc: DEFAULT_PRERENDER_IMAGE
				}
			],
			pagination: {
				itemsPerPage: 24,
				totalItems: 2,
				totalPages: 1,
				page: 1,
				query: "",
				taxonomyId: "",
				categoryId: "",
				taxonomies: [],
				categories: []
			},
			enableTaxonomyFilter: true,
			enableCategoryFilter: true,
			searchPlaceholder: "Rechercher",
			cardType: "ActivityGrid"
		},
		fields: [
			{
				type: "select",
				label: "Mode",
				path: ["content", "mode"],
				options: [
					{ label: "Manual", value: "manual" },
					{ label: "Dynamic", value: "dynamic" }
				],
				helper: "Manual uses the content below. Dynamic pulls items from a content type."
			},
			{
				type: "object",
				label: "Dynamic settings",
				path: ["content", "dynamicComponent"],
				showWhen: { path: ["content", "mode"], equals: "dynamic" },
				fields: [
					{
						type: "contentTypeSelect",
						label: "Content type",
						path: ["contentTypeKey"],
						helper: "Select the content type to pull items from."
					},
					{
						type: "dynamicPageSelect",
						label: "Dynamic page",
						path: ["dynamicPageId"],
						contentTypePath: ["content", "dynamicComponent", "contentTypeKey"],
						allowEmpty: true,
						helper: "Pick the dynamic page template to link to (same content type)."
					},
					{
						type: "contentFieldSelect",
						label: "Sort field (date)",
						path: ["sortField"],
						contentTypePath: ["content", "dynamicComponent", "contentTypeKey"],
						fieldTypes: ["date"],
						includeBaseFields: false,
						allowEmpty: true,
						valueFormat: "raw",
						helper: "Latest first (used for pagination order)."
					},
					{
						type: "array",
						label: "Filters",
						itemLabel: "Filter",
						itemType: "object",
						defaultItem: { field: "", value: "" },
						path: ["filters"],
						fields: [
							{
								type: "contentFieldSelect",
								label: "Field",
								path: ["field"],
								contentTypePath: ["content", "dynamicComponent", "contentTypeKey"],
								includeBaseFields: true,
								allowEmpty: true,
								valueFormat: "raw"
							},
							{
								type: "contentFieldValue",
								label: "Value",
								path: ["value"],
								contentTypePath: ["content", "dynamicComponent", "contentTypeKey"],
								fieldPath: ["field"]
							}
						]
					},
					{
						type: "number",
						label: "Items per page",
						path: ["itemsPerPage"],
						helper: "Number of cards shown per page (default 24)."
					},
					{
						type: "select",
						label: "Taxonomy filter",
						path: ["enableTaxonomyFilter"],
						options: [
							{ label: "Enabled", value: "true" },
							{ label: "Disabled", value: "false" }
						],
						helper: "Show taxonomy filter dropdown."
					},
					{
						type: "select",
						label: "Category filter",
						path: ["enableCategoryFilter"],
						options: [
							{ label: "Enabled", value: "true" },
							{ label: "Disabled", value: "false" }
						],
						helper: "Show category filter dropdown."
					},
					{
						type: "text",
						label: "Search placeholder",
						path: ["searchPlaceholder"],
						helper: "Label shown inside the search input."
					},
					{
						type: "select",
						label: "Card layout",
						path: ["cardType"],
						options: [{ label: "Activity Grid", value: "ActivityGrid" }],
						helper: "Additional layouts will be added later."
					},
					{
						type: "object",
						label: "Field mapping",
						path: ["fieldMapping"],
						fields: [
							{
								type: "contentFieldSelect",
								label: "Title field",
								path: ["title"],
								contentTypePath: ["content", "dynamicComponent", "contentTypeKey"],
								fieldTypes: ["text"],
								includeBaseFields: true,
								valueFormat: "raw",
								helper: "Select the field to use for the title."
							},
							{
								type: "contentFieldSelect",
								label: "Description field",
								path: ["description"],
								contentTypePath: ["content", "dynamicComponent", "contentTypeKey"],
								fieldTypes: ["text"],
								includeBaseFields: true,
								valueFormat: "raw",
								helper: "Select the field to use for the description."
							},
							{
								type: "contentFieldSelect",
								label: "Image field",
								path: ["image"],
								contentTypePath: ["content", "dynamicComponent", "contentTypeKey"],
								fieldTypes: ["image"],
								includeBaseFields: false,
								valueFormat: "raw",
								helper: "Select the image field to use."
							},
							{
								type: "contentFieldSelect",
								label: "Image alt field",
								path: ["imageAlt"],
								contentTypePath: ["content", "dynamicComponent", "contentTypeKey"],
								fieldTypes: ["text"],
								includeBaseFields: true,
								valueFormat: "raw",
								helper: "Optional alt text field (falls back to title)."
							},
							{
								type: "contentFieldSelect",
								label: "Link field",
								path: ["href"],
								contentTypePath: ["content", "dynamicComponent", "contentTypeKey"],
								fieldTypes: ["text", "url"],
								includeBaseFields: true,
								valueFormat: "raw",
								helper: "Select the field used for the link (slug/path/url)."
							}
						]
					}
				]
			},
			{
				type: "array",
				label: "Activity items",
				itemLabel: "Activity",
				itemType: "object",
				defaultItem: { title: "", description: "", href: { slug: "" }, imageSrc: DEFAULT_PRERENDER_IMAGE },
				path: ["content", "items"],
				showWhen: { path: ["content", "mode"], equals: "manual" },
				fields: [
					{ type: "text", label: "Title", path: ["title"] },
					{ type: "textarea", label: "Description", path: ["description"] },
					{
						type: "href",
						label: "Destination",
						path: ["href"],
						helper: "Choose an activity page or external destination."
					},
					{
						type: "image",
						label: "Image src",
						path: ["imageSrc"],
						helper: "Select an image from storage (CDN key only)."
					}
				]
			}
		],
		previewComponents: withPreview(ComponentTypes.PaginatedActivityGrid, {
			mode: "dynamic",
			items: [
				{
					title: "Activity 1",
					description: "Activity description 1",
					href: { slug: "/activity-1" },
					imageSrc: DEFAULT_PRERENDER_IMAGE
				},
				{
					title: "Activity 2",
					description: "Activity description 2",
					href: { slug: "/activity-2" },
					imageSrc: DEFAULT_PRERENDER_IMAGE
				}
			],
			pagination: {
				itemsPerPage: 24,
				totalItems: 2,
				totalPages: 1,
				page: 1,
				query: "",
				taxonomyId: "",
				categoryId: "",
				taxonomies: [],
				categories: []
			},
			enableTaxonomyFilter: true,
			enableCategoryFilter: true,
			searchPlaceholder: "Rechercher",
			cardType: "ActivityGrid"
		})
	},
	{
		type: ComponentTypes.PersonCardGrid,
		category: "cartes",
		skeleton: { kind: "cards", count: 4, withImage: true },
		title: "Grille de personnes",
		description: "Grille de cartes personne/fonction avec sous-titre en bleu.",
		tags: ["grid", "people", "roles", "card"],
		defaultContent: {
			items: [
				{
					title: "Person 1",
					description: "Role or title",
					href: { slug: "/person-1" },
					imageSrc: DEFAULT_PRERENDER_IMAGE
				},
				{
					title: "Person 2",
					description: "Role or title",
					href: { slug: "/person-2" },
					imageSrc: DEFAULT_PRERENDER_IMAGE
				}
			]
		},
		fields: [
			{
				type: "array",
				label: "Person items",
				itemLabel: "Person",
				itemType: "object",
				defaultItem: { title: "", description: "", href: { slug: "" }, imageSrc: DEFAULT_PRERENDER_IMAGE },
				path: ["content", "items"],
				fields: [
					{ type: "text", label: "Name", path: ["title"] },
					{ type: "textarea", label: "Role / subtitle", path: ["description"] },
					{
						type: "href",
						label: "Destination",
						path: ["href"],
						helper: "Choose a profile page or external destination."
					},
					{
						type: "image",
						label: "Image",
						path: ["imageSrc"],
						helper: "Select an image from storage (CDN key only)."
					}
				]
			}
		],
		previewComponents: withPreview(ComponentTypes.PersonCardGrid, {
			items: [
				{
					title: "Person 1",
					description: "Role or title",
					href: { slug: "/person-1" },
					imageSrc: DEFAULT_PRERENDER_IMAGE
				},
				{
					title: "Person 2",
					description: "Role or title",
					href: { slug: "/person-2" },
					imageSrc: DEFAULT_PRERENDER_IMAGE
				}
			]
		})
	},
	{
		type: ComponentTypes.EventGrid,
		category: "cartes",
		skeleton: { kind: "cards", count: 3 },
		title: "Grille d'événements",
		description: "Liste compacte des événements à venir.",
		tags: ["events"],
		defaultContent: {
			items: [
				{
					tag: "EVENT",
					title: "Event title 1",
					location: "Location",
					href: { slug: "/events/event-1" },
					startDay: 1,
					endDay: 1,
					monthLabel: "JAN"
				},
				{
					tag: "EVENT",
					title: "Event title 2",
					location: "Location",
					href: { slug: "/events/event-2" },
					startDay: 2,
					endDay: 2,
					monthLabel: "FEB"
				}
			],
			ctaLabel: "All events",
			ctaHref: { slug: "/events" }
		},
		fields: [
			{
				type: "array",
				label: "Events",
				itemLabel: "Event",
				itemType: "object",
				defaultItem: {
					tag: "",
					title: "",
					location: "",
					href: { slug: "" },
					startDay: 1,
					endDay: 1,
					monthLabel: ""
				},
				path: ["content", "items"],
				fields: [
					{ type: "text", label: "Tag", path: ["tag"] },
					{ type: "text", label: "Title", path: ["title"] },
					{ type: "text", label: "Location", path: ["location"] },
					{
						type: "href",
						label: "Destination",
						path: ["href"],
						helper: "Choose an event page or external registration link."
					},
					{ type: "number", label: "Start day", path: ["startDay"] },
					{ type: "number", label: "End day", path: ["endDay"] },
					{ type: "text", label: "Month label", path: ["monthLabel"], placeholder: "MMM" }
				]
			},
			{ type: "text", label: "CTA label", path: ["content", "ctaLabel"], placeholder: "CTA label" },
			{
				type: "href",
				label: "CTA destination",
				path: ["content", "ctaHref"],
				helper: "Link to the events listing or an external calendar page."
			}
		],
		previewComponents: withPreview(ComponentTypes.EventGrid, {
			items: [
				{
					tag: "EVENT",
					title: "Event title 1",
					location: "Location",
					href: { slug: "/events/event-1" },
					startDay: 1,
					endDay: 1,
					monthLabel: "JAN"
				},
				{
					tag: "EVENT",
					title: "Event title 2",
					location: "Location",
					href: { slug: "/events/event-2" },
					startDay: 2,
					endDay: 2,
					monthLabel: "FEB"
				}
			],
			ctaLabel: "All events",
			ctaHref: { slug: "/events" }
		})
	},
	{
		type: ComponentTypes.ArticleGrid,
		category: "cartes",
		skeleton: { kind: "cards", count: 4, withImage: true },
		title: "Grille d'articles",
		description: "Quatre cartes d'articles avec catégorie, date et lien.",
		tags: ["articles", "grid"],
		defaultContent: {
			items: [
				{
					category: "Category",
					title: "Article title 1",
					excerpt: "Article excerpt 1",
					author: "",
					authorRole: "",
					date: "01 Jan 2025",
					href: { slug: "/articles/article-1" },
					imageSrc: DEFAULT_PRERENDER_IMAGE
				},
				{
					category: "Category",
					title: "Article title 2",
					excerpt: "Article excerpt 2",
					author: "",
					authorRole: "",
					date: "01 Jan 2025",
					href: { slug: "/articles/article-2" },
					imageSrc: DEFAULT_PRERENDER_IMAGE
				}
			],
			ctaLabel: "View all",
			ctaHref: { slug: "/articles" }
		},
		fields: [
			{
				type: "array",
				label: "Articles",
				itemLabel: "Article",
				itemType: "object",
				defaultItem: {
					category: "",
					title: "",
					excerpt: "",
					author: "",
					authorRole: "",
					date: "",
					href: { slug: "" },
					imageSrc: DEFAULT_PRERENDER_IMAGE
				},
				path: ["content", "items"],
				fields: [
					{ type: "text", label: "Category", path: ["category"] },
					{ type: "text", label: "Title", path: ["title"] },
					{ type: "textarea", label: "Excerpt", path: ["excerpt"] },
					{ type: "text", label: "Date", path: ["date"] },
					{
						type: "href",
						label: "Destination",
						path: ["href"],
						helper: "Link to an article page or external source."
					},
					{
						type: "image",
						label: "Image src",
						path: ["imageSrc"],
						helper: "Select an image from storage (CDN key only)."
					}
				]
			},
			{ type: "text", label: "CTA label", path: ["content", "ctaLabel"], placeholder: "CTA label" },
			{
				type: "href",
				label: "CTA destination",
				path: ["content", "ctaHref"],
				helper: "Link to the blog index or another page."
			}
		],
		previewComponents: withPreview(ComponentTypes.ArticleGrid, {
			items: [
				{
					category: "Category",
					title: "Article title 1",
					excerpt: "Article excerpt 1",
					author: "",
					authorRole: "",
					date: "01 Jan 2025",
					href: { slug: "/articles/article-1" },
					imageSrc: DEFAULT_PRERENDER_IMAGE
				},
				{
					category: "Category",
					title: "Article title 2",
					excerpt: "Article excerpt 2",
					author: "",
					authorRole: "",
					date: "01 Jan 2025",
					href: { slug: "/articles/article-2" },
					imageSrc: DEFAULT_PRERENDER_IMAGE
				}
			],
			ctaLabel: "View all",
			ctaHref: { slug: "/articles" }
		})
	},
	{
		type: ComponentTypes.LatestArticleGrid,
		category: "cartes",
		skeleton: { kind: "cards", count: 4, withImage: true },
		title: "Grille dynamique (contenus récents)",
		description: "Grille qui se remplit automatiquement des derniers contenus d'un type donné.",
		tags: ["articles", "grid", "dynamic"],
		defaultContent: {
			mode: "dynamic",
			dynamicComponent: {
				contentTypeKey: "",
				dynamicPageId: "",
				sortField: "",
				filters: [],
				itemsCount: 3,
				fieldMapping: {
					category: "",
					title: "",
					excerpt: "",
					author: "",
					authorRole: "",
					date: "",
					image: "",
					href: ""
				}
			},
			items: [
				{
					category: "Category",
					title: "Article title 1",
					excerpt: "Article excerpt 1",
					author: "",
					authorRole: "",
					date: "01 Jan 2025",
					href: { slug: "/articles/article-1" },
					imageSrc: DEFAULT_PRERENDER_IMAGE
				},
				{
					category: "Category",
					title: "Article title 2",
					excerpt: "Article excerpt 2",
					author: "",
					authorRole: "",
					date: "01 Jan 2025",
					href: { slug: "/articles/article-2" },
					imageSrc: DEFAULT_PRERENDER_IMAGE
				}
			],
			ctaLabel: "View all",
			ctaHref: { slug: "/articles" }
		},
		fields: [
			{
				type: "select",
				label: "Mode",
				path: ["content", "mode"],
				options: [
					{ label: "Manual", value: "manual" },
					{ label: "Dynamic", value: "dynamic" }
				],
				helper: "Manual uses content below. Dynamic pulls latest items from a content type."
			},
			{
				type: "object",
				label: "Dynamic settings",
				path: ["content", "dynamicComponent"],
				showWhen: { path: ["content", "mode"], equals: "dynamic" },
				fields: [
					{
						type: "contentTypeSelect",
						label: "Content type",
						path: ["contentTypeKey"],
						helper: "Select the content type to pull article items from."
					},
					{
						type: "dynamicPageSelect",
						label: "Dynamic page",
						path: ["dynamicPageId"],
						contentTypePath: ["content", "dynamicComponent", "contentTypeKey"],
						allowEmpty: true,
						helper: "Optional dynamic page template used to build article links."
					},
					{
						type: "contentFieldSelect",
						label: "Sort field (date)",
						path: ["sortField"],
						contentTypePath: ["content", "dynamicComponent", "contentTypeKey"],
						fieldTypes: ["date"],
						includeBaseFields: false,
						allowEmpty: true,
						valueFormat: "raw",
						helper: "Latest first. Falls back to last updated when empty."
					},
					{
						type: "number",
						label: "Items count",
						path: ["itemsCount"],
						helper: "Number of latest cards to show (default 3)."
					},
					{
						type: "array",
						label: "Filters",
						itemLabel: "Filter",
						itemType: "object",
						defaultItem: { field: "", value: "" },
						path: ["filters"],
						fields: [
							{
								type: "contentFieldSelect",
								label: "Field",
								path: ["field"],
								contentTypePath: ["content", "dynamicComponent", "contentTypeKey"],
								includeBaseFields: true,
								allowEmpty: true,
								valueFormat: "raw"
							},
							{
								type: "contentFieldValue",
								label: "Value",
								path: ["value"],
								contentTypePath: ["content", "dynamicComponent", "contentTypeKey"],
								fieldPath: ["field"]
							}
						]
					},
					{
						type: "object",
						label: "Field mapping",
						path: ["fieldMapping"],
						fields: [
							{
								type: "contentFieldSelect",
								label: "Category field",
								path: ["category"],
								contentTypePath: ["content", "dynamicComponent", "contentTypeKey"],
								fieldTypes: ["text"],
								includeBaseFields: true,
								allowEmpty: true,
								valueFormat: "raw"
							},
							{
								type: "contentFieldSelect",
								label: "Title field",
								path: ["title"],
								contentTypePath: ["content", "dynamicComponent", "contentTypeKey"],
								fieldTypes: ["text"],
								includeBaseFields: true,
								allowEmpty: true,
								valueFormat: "raw"
							},
							{
								type: "contentFieldSelect",
								label: "Excerpt field",
								path: ["excerpt"],
								contentTypePath: ["content", "dynamicComponent", "contentTypeKey"],
								fieldTypes: ["text"],
								includeBaseFields: true,
								allowEmpty: true,
								valueFormat: "raw"
							},
							{
								type: "contentFieldSelect",
								label: "Date field",
								path: ["date"],
								contentTypePath: ["content", "dynamicComponent", "contentTypeKey"],
								fieldTypes: ["date", "text"],
								includeBaseFields: true,
								allowEmpty: true,
								valueFormat: "raw"
							},
							{
								type: "contentFieldSelect",
								label: "Image field",
								path: ["image"],
								contentTypePath: ["content", "dynamicComponent", "contentTypeKey"],
								fieldTypes: ["image"],
								includeBaseFields: false,
								allowEmpty: true,
								valueFormat: "raw"
							},
							{
								type: "contentFieldSelect",
								label: "Link field",
								path: ["href"],
								contentTypePath: ["content", "dynamicComponent", "contentTypeKey"],
								fieldTypes: ["text", "url"],
								includeBaseFields: true,
								allowEmpty: true,
								valueFormat: "raw"
							}
						]
					}
				]
			},
			{
				type: "array",
				label: "Articles",
				itemLabel: "Article",
				itemType: "object",
				defaultItem: {
					category: "",
					title: "",
					excerpt: "",
					author: "",
					authorRole: "",
					date: "",
					href: { slug: "" },
					imageSrc: DEFAULT_PRERENDER_IMAGE
				},
				path: ["content", "items"],
				showWhen: { path: ["content", "mode"], equals: "manual" },
				fields: [
					{ type: "text", label: "Category", path: ["category"] },
					{ type: "text", label: "Title", path: ["title"] },
					{ type: "textarea", label: "Excerpt", path: ["excerpt"] },
					{ type: "text", label: "Date", path: ["date"] },
					{
						type: "href",
						label: "Destination",
						path: ["href"],
						helper: "Link to an article page or external source."
					},
					{
						type: "image",
						label: "Image src",
						path: ["imageSrc"],
						helper: "Select an image from storage (CDN key only)."
					}
				]
			},
			{ type: "text", label: "CTA label", path: ["content", "ctaLabel"], placeholder: "CTA label" },
			{
				type: "href",
				label: "CTA destination",
				path: ["content", "ctaHref"],
				helper: "Link to the blog index or another page."
			}
		],
		previewComponents: withPreview(ComponentTypes.LatestArticleGrid, {
			mode: "dynamic",
			dynamicComponent: {
				contentTypeKey: "",
				dynamicPageId: "",
				sortField: "",
				filters: [],
				itemsCount: 3,
				fieldMapping: {
					category: "",
					title: "",
					excerpt: "",
					author: "",
					authorRole: "",
					date: "",
					image: "",
					href: ""
				}
			},
			items: [
				{
					category: "Category",
					title: "Article title 1",
					excerpt: "Article excerpt 1",
					author: "",
					authorRole: "",
					date: "01 Jan 2025",
					href: { slug: "/articles/article-1" },
					imageSrc: DEFAULT_PRERENDER_IMAGE
				},
				{
					category: "Category",
					title: "Article title 2",
					excerpt: "Article excerpt 2",
					author: "",
					authorRole: "",
					date: "01 Jan 2025",
					href: { slug: "/articles/article-2" },
					imageSrc: DEFAULT_PRERENDER_IMAGE
				}
			],
			ctaLabel: "View all",
			ctaHref: { slug: "/articles" }
		})
	},
	{
		type: ComponentTypes.IconCardGrid,
		category: "cartes",
		skeleton: { kind: "cards", count: 4 },
		title: "Grille de cartes à icônes",
		description: "Piliers de politique représentés par des tuiles à icône.",
		tags: ["icons", "grid", "card"],
		defaultContent: {
			items: [
				{ icon: "ShieldCheck", title: "Card title 1", text: "Card text 1" },
				{ icon: "Gauge", title: "Card title 2", text: "Card text 2" },
				{ icon: "Banknote", title: "Card title 3", text: "Card text 3" }
			],
			cardsPerRowTablet: 2,
			cardsPerRowDesktop: 2,
			centerLastRow: false
		},
		fields: [
			{
				type: "select",
				label: "Cards per row (tablet)",
				path: ["content", "cardsPerRowTablet"],
				options: [
					{ label: "2", value: "2" },
					{ label: "3", value: "3" },
					{ label: "4", value: "4" }
				]
			},
			{
				type: "select",
				label: "Cards per row (desktop)",
				path: ["content", "cardsPerRowDesktop"],
				options: [
					{ label: "2", value: "2" },
					{ label: "3", value: "3" },
					{ label: "4", value: "4" }
				]
			},
			{
				type: "select",
				label: "Center last row",
				path: ["content", "centerLastRow"],
				options: [
					{ label: "No", value: "false" },
					{ label: "Yes", value: "true" }
				],
				helper: "When the last row has fewer items, center them. Default: no."
			},
			{
				type: "array",
				label: "Icon cards",
				itemLabel: "Card",
				itemType: "object",
				defaultItem: { icon: "ShieldCheck", title: "", text: "" },
				path: ["content", "items"],
				fields: [
					{ type: "select", label: "Icon", path: ["icon"], options: ICON_CARD_ICONS },
					{ type: "text", label: "Title", path: ["title"] },
					{ type: "textarea", label: "Text", path: ["text"] }
				]
			}
		],
		previewComponents: withPreview(ComponentTypes.IconCardGrid, {
			items: [
				{ icon: "ShieldCheck", title: "Card title 1", text: "Card text 1" },
				{ icon: "Gauge", title: "Card title 2", text: "Card text 2" },
				{ icon: "Banknote", title: "Card title 3", text: "Card text 3" }
			],
			cardsPerRowTablet: 2,
			cardsPerRowDesktop: 2
		})
	},
	{
		type: ComponentTypes.StatsGrid,
		category: "donnees",
		skeleton: { kind: "chart" },
		title: "Grille de statistiques",
		description: "Tuiles d'indicateurs pour les statistiques clés.",
		tags: ["stats", "grid"],
		defaultContent: {
			items: [
				{ title: "Metric 1", value: "0", meta: "Meta" },
				{ title: "Metric 2", value: "0", meta: "Meta" },
				{ title: "Metric 3", value: "0", meta: "Meta" }
			]
		},
		fields: [
			{
				type: "array",
				label: "Stat items",
				itemLabel: "Stat",
				itemType: "object",
				defaultItem: { title: "", value: "", meta: "" },
				path: ["content", "items"],
				fields: [
					{ type: "text", label: "Title", path: ["title"] },
					{ type: "text", label: "Value", path: ["value"] },
					{ type: "text", label: "Meta", path: ["meta"] }
				]
			}
		],
		previewComponents: withPreview(ComponentTypes.StatsGrid, {
			items: [
				{ title: "Metric 1", value: "0", meta: "Meta" },
				{ title: "Metric 2", value: "0", meta: "Meta" },
				{ title: "Metric 3", value: "0", meta: "Meta" }
			]
		})
	},
	{
		type: ComponentTypes.SplitImageCallout,
		category: "texte",
		skeleton: { kind: "columns", spans: [6, 6] },
		title: "Encadré image + texte",
		description: "Mise en page en deux colonnes avec appel a l'action et image.",
		tags: ["callout", "image"],
		defaultContent: {
			imageSrc: DEFAULT_PRERENDER_IMAGE,
			imageAlt: "Image",
			title: "Callout title",
			text: "Callout text",
			ctaLabel: "Learn more",
			ctaHref: { slug: "/callout" },
			calloutVariant: "default",
			panelBackground: "default",
			buttonColor: "default"
		},
		fields: [
			{
				type: "image",
				label: "Image src",
				path: ["content", "imageSrc"],
				helper: "Select an image from storage (CDN key only)."
			},
			{ type: "text", label: "Image alt", path: ["content", "imageAlt"] },
			{ type: "text", label: "Title", path: ["content", "title"] },
			{ type: "textarea", label: "Text", path: ["content", "text"] },
			{ type: "text", label: "CTA label", path: ["content", "ctaLabel"] },
			{ type: "text", label: "CTA slug", path: ["content", "ctaHref", "slug"], placeholder: "/path" },
			{ type: "text", label: "CTA external URL", path: ["content", "ctaHref", "external"], placeholder: "https://..." },
			{
				type: "select",
				label: "Callout style",
				path: ["content", "calloutVariant"],
				options: [
					{ label: "Default (blue panel)", value: "default" },
					{ label: "Transparent", value: "transparent" },
					{ label: "Full width bg (#F3F7FC)", value: "fullWidth" }
				]
			},
			{
				type: "select",
				label: "Panel background",
				path: ["content", "panelBackground"],
				options: [
					{ label: "Default blue (#005CA9)", value: "default" },
					{ label: "Light (#EDF7FC)", value: "light" },
					{ label: "Transparent", value: "transparent" }
				]
			},
			{
				type: "select",
				label: "Button color",
				path: ["content", "buttonColor"],
				options: [
					{ label: "Default orange (#F9A21B)", value: "default" },
					{ label: "Blue (#005CA9)", value: "blue" }
				]
			},
			{
				type: "select",
				label: "Image fit text height (desktop/tablet)",
				path: ["content", "imageFitTextHeight"],
				options: [
					{ label: "No", value: "false" },
					{ label: "Yes : image height matches text panel", value: "true" }
				],
				helper: "When on, both columns share the same height on desktop/tablet; image fills and crops to match. On mobile the image keeps normal visible height (16:9)."
			}
		],
		previewComponents: withPreview(ComponentTypes.SplitImageCallout, {
			imageSrc: DEFAULT_PRERENDER_IMAGE,
			imageAlt: "Image",
			title: "Callout title",
			text: "Callout text",
			ctaLabel: "Learn more",
			ctaHref: "#"
		})
	},
	{
		type: ComponentTypes.TextCardGrid,
		category: "cartes",
		skeleton: { kind: "cards", count: 3 },
		title: "Grille de cartes texte",
		description: "Grille de cartes sans icône : titre, texte et flèche vers le bas.",
		tags: ["grid", "cards", "card"],
		defaultContent: {
			items: [
				{ title: "Card title 1", text: "Card text 1" },
				{ title: "Card title 2", text: "Card text 2" },
				{ title: "Card title 3", text: "Card text 3" }
			],
			cardsPerRowTablet: 3,
			cardsPerRowDesktop: 3
		},
		fields: [
			{
				type: "select",
				label: "Cards per row (tablet)",
				path: ["content", "cardsPerRowTablet"],
				options: [
					{ label: "2", value: "2" },
					{ label: "3", value: "3" },
					{ label: "4", value: "4" }
				]
			},
			{
				type: "select",
				label: "Cards per row (desktop)",
				path: ["content", "cardsPerRowDesktop"],
				options: [
					{ label: "2", value: "2" },
					{ label: "3", value: "3" },
					{ label: "4", value: "4" }
				]
			},
			{
				type: "array",
				label: "Cards",
				itemLabel: "Card",
				itemType: "object",
				defaultItem: { title: "", text: "" },
				path: ["content", "items"],
				fields: [
					{ type: "text", label: "Title", path: ["title"] },
					{ type: "textarea", label: "Text", path: ["text"] },
					{ type: "href", label: "Link (optional)", path: ["href"] },
					{
						type: "text",
						label: "Icône (optionnel)",
						path: ["icon"],
						helper:
							"Nom d'icône : ArrowLeftRight, Banknote, BookOpenCheck, Building2, FileText, HandCoins, Handshake, Landmark, PiggyBank, Scale, Send, ShieldCheck, Smartphone, TrendingUp. Vide = déduite du titre."
					},
					{
						type: "text",
						label: "Libellé du lien (optionnel)",
						path: ["linkLabel"],
						helper: "Texte du lien du bandeau bleu. Défaut : « Tout consulter »."
					},
					{
						type: "text",
						label: "Sur-titre de la liste (optionnel)",
						path: ["entriesEyebrow"],
						helper: "Défaut : « Répertoire officiel ». Utilisé seulement si la carte a des liens ci-dessous."
					},
					{
						type: "text",
						label: "Titre de la liste (optionnel)",
						path: ["entriesTitle"],
						helper: "Défaut : « Services et registres »."
					},
					{
						type: "array",
						label: "Liens du panneau (optionnel)",
						itemLabel: "Lien",
						itemType: "object",
						defaultItem: { title: "", text: "" },
						path: ["entries"],
						helper:
							"Ajouter des liens transforme la carte en PANNEAU : bandeau bleu à gauche (titre, texte, lien de la carte) et liste de ces liens à droite. Laisser vide pour garder une tuile simple.",
						fields: [
							{ type: "text", label: "Titre", path: ["title"] },
							{ type: "textarea", label: "Texte", path: ["text"] },
							{ type: "href", label: "Lien", path: ["href"] },
							{ type: "text", label: "Icône (optionnel)", path: ["icon"] }
						]
					}
				]
			}
		],
		previewComponents: withPreview(ComponentTypes.TextCardGrid, {
			items: [
				{ title: "Card title 1", text: "Card text 1" },
				{ title: "Card title 2", text: "Card text 2" },
				{ title: "Card title 3", text: "Card text 3" }
			],
			cardsPerRowTablet: 3,
			cardsPerRowDesktop: 3
		})
	},
	{
		type: ComponentTypes.LabeledTextGrid,
		category: "cartes",
		skeleton: { kind: "cards", count: 3 },
		title: "Grille de textes étiquetés",
		description: "Grille de blocs de texte : titre en bleu, lignes de corps en noir.",
		tags: ["grid", "text"],
		defaultContent: {
			items: [
				{ title: "Adresse postale", lines: ["2697 Kinshasa 1", "République Démocratique du Congo"] },
				{ title: "Livraisons", lines: ["2697 Kinshasa 1", "République Démocratique du Congo"] }
			],
			cardsPerRowTablet: 2,
			cardsPerRowDesktop: 2,
			centerLastRow: false
		},
		fields: [
			{
				type: "select",
				label: "Cards per row (tablet)",
				path: ["content", "cardsPerRowTablet"],
				options: [
					{ label: "2", value: "2" },
					{ label: "3", value: "3" },
					{ label: "4", value: "4" }
				]
			},
			{
				type: "select",
				label: "Cards per row (desktop)",
				path: ["content", "cardsPerRowDesktop"],
				options: [
					{ label: "2", value: "2" },
					{ label: "3", value: "3" },
					{ label: "4", value: "4" }
				]
			},
			{
				type: "select",
				label: "Center last row",
				path: ["content", "centerLastRow"],
				options: [
					{ label: "No", value: "false" },
					{ label: "Yes", value: "true" }
				],
				helper: "When the last row has fewer items, center them. Default: no."
			},
			{
				type: "array",
				label: "Items",
				itemLabel: "Block",
				itemType: "object",
				defaultItem: { title: "", lines: [] },
				path: ["content", "items"],
				fields: [
					{ type: "text", label: "Title", path: ["title"], placeholder: "e.g. Adresse postale" },
					{
						type: "array",
						label: "Lines",
						itemLabel: "Line",
						itemType: "text",
						defaultItem: "",
						path: ["lines"]
					}
				]
			}
		],
		previewComponents: withPreview(ComponentTypes.LabeledTextGrid, {
			items: [
				{ title: "Adresse postale", lines: ["2697 Kinshasa 1", "République Démocratique du Congo"] },
				{ title: "Livraisons", lines: ["2697 Kinshasa 1", "République Démocratique du Congo"] }
			],
			cardsPerRowTablet: 2,
			cardsPerRowDesktop: 2
		})
	},
	{
		type: ComponentTypes.CompanyReportGrid,
		category: "cartes",
		skeleton: { kind: "cards", count: 3 },
		title: "Grille de rapports",
		description: "Grille de cartes de rapport : titre, année, date de publication et téléchargement.",
		tags: ["grid", "reports", "documents"],
		defaultContent: {
			items: [
				{ title: "Rapport d'entreprise", year: "2025", publishedDate: "21-11-2025", href: { slug: "/docs/rapport-2025" } },
				{ title: "Rapport d'entreprise", year: "2024", publishedDate: "21-11-2024", href: { slug: "/docs/rapport-2024" } },
				{ title: "Rapport d'entreprise", year: "2023", publishedDate: "21-11-2023", href: { slug: "/docs/rapport-2023" } }
			],
			cardsPerRowTablet: 3,
			cardsPerRowDesktop: 3,
			downloadLabel: "Télécharger",
			centerLastRow: false
		},
		fields: [
			{
				type: "select",
				label: "Cards per row (tablet)",
				path: ["content", "cardsPerRowTablet"],
				options: [
					{ label: "2", value: "2" },
					{ label: "3", value: "3" },
					{ label: "4", value: "4" }
				]
			},
			{
				type: "select",
				label: "Cards per row (desktop)",
				path: ["content", "cardsPerRowDesktop"],
				options: [
					{ label: "2", value: "2" },
					{ label: "3", value: "3" },
					{ label: "4", value: "4" }
				]
			},
			{
				type: "select",
				label: "Center last row",
				path: ["content", "centerLastRow"],
				options: [
					{ label: "No", value: "false" },
					{ label: "Yes", value: "true" }
				],
				helper: "When the last row has fewer items, center them. Default: no."
			},
			{ type: "text", label: "Download button label", path: ["content", "downloadLabel"], placeholder: "Télécharger" },
			{
				type: "array",
				label: "Report cards",
				itemLabel: "Report",
				itemType: "object",
				defaultItem: { title: "Rapport d'entreprise", year: "", publishedDate: "", href: { slug: "" } },
				path: ["content", "items"],
				fields: [
					{ type: "text", label: "Title", path: ["title"], placeholder: "Rapport d'entreprise" },
					{ type: "text", label: "Year", path: ["year"], placeholder: "2025" },
					{ type: "text", label: "Published date", path: ["publishedDate"], placeholder: "21-11-2025" },
					{
						type: "file",
						label: "Pick PDF from storage",
						path: ["href"],
						folder: "documents",
						helper: "Select the report PDF."
					},
					{
						type: "href",
						label: "Or destination (page / PDF URL)",
						path: ["href"]
					}
				]
			}
		],
		previewComponents: withPreview(ComponentTypes.CompanyReportGrid, {
			items: [
				{ title: "Rapport d'entreprise", year: "2025", publishedDate: "21-11-2025", href: { slug: "/docs/rapport-2025" } },
				{ title: "Rapport d'entreprise", year: "2024", publishedDate: "21-11-2024", href: { slug: "/docs/rapport-2024" } },
				{ title: "Rapport d'entreprise", year: "2023", publishedDate: "21-11-2023", href: { slug: "/docs/rapport-2023" } },
				{ title: "Rapport d'entreprise", year: "2022", publishedDate: "21-11-2022", href: { slug: "/docs/rapport-2022" } },
				{ title: "Rapport d'entreprise", year: "2021", publishedDate: "21-11-2021", href: { slug: "/docs/rapport-2021" } },
				{ title: "Rapport d'entreprise", year: "2020", publishedDate: "21-11-2020", href: { slug: "/docs/rapport-2020" } }
			],
			cardsPerRowTablet: 3,
			cardsPerRowDesktop: 3,
			downloadLabel: "Télécharger"
		})
	},
	{
		type: ComponentTypes.TextWithSideImage,
		category: "texte",
		skeleton: { kind: "columns", spans: [8, 4] },
		title: "Texte avec image latérale",
		description: "Texte en deux colonnes avec image de portrait.",
		tags: ["profile", "text"],
		defaultContent: {
			imageSrc: DEFAULT_PRERENDER_IMAGE,
			imageAlt: "Portrait",
			firstParagraph: "Paragraph 1",
			secondParagraph: "Paragraph 2",
			governorName: "Name",
			governorHref: { slug: "/profile" }
		},
		fields: [
			{
				type: "image",
				label: "Image src",
				path: ["content", "imageSrc"],
				helper: "Select an image from storage (CDN key only)."
			},
			{ type: "text", label: "Image alt", path: ["content", "imageAlt"] },
			{ type: "textarea", label: "First paragraph", path: ["content", "firstParagraph"] },
			{ type: "textarea", label: "Second paragraph", path: ["content", "secondParagraph"] },
			{ type: "text", label: "Governor name", path: ["content", "governorName"] },
			{ type: "text", label: "Governor slug", path: ["content", "governorHref", "slug"], placeholder: "/path" },
			{ type: "text", label: "Governor external URL", path: ["content", "governorHref", "external"], placeholder: "https://..." }
		],
		previewComponents: withPreview(ComponentTypes.TextWithSideImage, {
			imageSrc: DEFAULT_PRERENDER_IMAGE,
			imageAlt: "Portrait",
			firstParagraph: "Paragraph 1",
			secondParagraph: "Paragraph 2",
			governorName: "Name",
			governorHref: "#"
		})
	},
	{
		type: ComponentTypes.TextWithSidePanel,
		category: "texte",
		skeleton: { kind: "columns", spans: [8, 4] },
		title: "Texte avec panneau latéral",
		description: "Deux tiers de texte avec un panneau latéral pour média et appel à l'action.",
		tags: ["text", "panel", "image"],
		defaultContent: {
			title: "Section title",
			paragraphs: [
				"Paragraph 1",
				"Paragraph 2",
				"Paragraph 3"
			],
			panelTitle: "Panel title",
			imageSrc: DEFAULT_PRERENDER_IMAGE,
			imageAlt: "Image",
			ctaLabel: "Learn more",
			ctaHref: { slug: "/link" }
		},
		fields: [
			{ type: "text", label: "Title", path: ["content", "title"] },
			{
				type: "array",
				label: "Paragraphes",
				itemLabel: "Paragraphe",
				itemType: "text",
				defaultItem: "",
				path: ["content", "paragraphs"],
				itemEditor: "richParagraph"
			},
			{ type: "text", label: "Panel title", path: ["content", "panelTitle"] },
			{
				type: "image",
				label: "Panel image",
				path: ["content", "imageSrc"],
				helper: "Select an image from storage (CDN key only)."
			},
			{ type: "text", label: "Image alt", path: ["content", "imageAlt"] },
			{ type: "text", label: "CTA label", path: ["content", "ctaLabel"] },
			{
				type: "href",
				label: "CTA destination",
				path: ["content", "ctaHref"],
				helper: "Select a page or external URL for the button."
			}
		],
		previewComponents: withPreview(ComponentTypes.TextWithSidePanel, {
			title: "Section title",
			paragraphs: [
				"Paragraph 1",
				"Paragraph 2",
				"Paragraph 3"
			],
			panelTitle: "Panel title",
			imageSrc: DEFAULT_PRERENDER_IMAGE,
			imageAlt: "Image",
			ctaLabel: "Learn more",
			ctaHref: "#"
		})
	},
	{
		type: ComponentTypes.BannerImage,
		category: "medias",
		skeleton: { kind: "image" },
		title: "Image bannière",
		description: "Bloc d'image bannière pleine largeur.",
		tags: ["image", "banner"],
		defaultContent: {
			src: DEFAULT_PRERENDER_IMAGE,
			alt: "Banner image",
			showLoadingCircle: false
		},
		fields: [
			{
				type: "image",
				label: "Image src",
				path: ["content", "src"],
				helper: "Select an image from storage (CDN key only)."
			},
			{ type: "text", label: "Image alt", path: ["content", "alt"] },
			{
				type: "select",
				label: "Show loading circle",
				path: ["content", "showLoadingCircle"],
				options: [
					{ label: "No", value: "false" },
					{ label: "Yes", value: "true" }
				],
				helper: "Show a loading circle while the image loads."
			}
		],
		previewComponents: withPreview(ComponentTypes.BannerImage, {
			src: DEFAULT_PRERENDER_IMAGE,
			alt: "Banner image",
			showLoadingCircle: false
		})
	},
	{
		type: "FullWidthImage" as ComponentType,
		category: "medias",
		primary: true,
		skeleton: { kind: "image" },
		title: "Image pleine largeur",
		description: "Bloc d'image pleine largeur.",
		tags: ["image", "full-width"],
		defaultContent: {
			src: DEFAULT_PRERENDER_IMAGE,
			alt: "Full width image",
			showLoadingCircle: false
		},
		fields: [
			{
				type: "image",
				label: "Image src",
				path: ["content", "src"],
				helper: "Select an image from storage (CDN key only)."
			},
			{ type: "text", label: "Image alt", path: ["content", "alt"] },
			{
				type: "select",
				label: "Show loading circle",
				path: ["content", "showLoadingCircle"],
				options: [
					{ label: "No", value: "false" },
					{ label: "Yes", value: "true" }
				],
				helper: "Show a loading circle while the image loads."
			}
		],
		previewComponents: withPreview("FullWidthImage" as ComponentType, {
			src: DEFAULT_PRERENDER_IMAGE,
			alt: "Full width image",
			showLoadingCircle: false
		})
	},
	{
		type: ComponentTypes.ContactForm,
		category: "formulaires",
		skeleton: { kind: "form" },
		title: "Formulaire de contact",
		description: "Formulaire de contact avec options de sujet.",
		tags: ["form", "contact"],
		defaultContent: {
			subjectOptions: [
				"Question générale",
				"Politique monétaire",
				"Supervision financière",
				"Billets et pièces",
				"Centrales des crédits",
				"Statistiques et publications",
				"Taux de change",
				"Réglementation du change",
				"Emploi et stages",
				"Presse et médias",
				"Musée et Hôtel des Monnaies",
				"Autre"
			],
			contactInfo: {
				address: "563, Bld. Colonel Tshatshi, Gombe, Kinshasa, BP 2697 Kinshasa 1",
				phone: "+243 82 990 21 43",
				email: "contact@bcc.cd"
			}
		},
		fields: [
			{
				type: "array",
				label: "Sujets proposés",
				itemLabel: "Sujet",
				itemType: "text",
				defaultItem: "",
				path: ["content", "subjectOptions"],
				helper: "Liste du menu de choix. Garder « Autre » en dernier."
			},
			{
				type: "text",
				label: "Sujet présélectionné",
				path: ["content", "defaultSubject"],
				helper: "Optionnel : sujet présélectionné à l'ouverture. Doit correspondre exactement à un des sujets ci-dessus."
			},
			{
				type: "text",
				label: "Libellé du choix de sujet",
				path: ["content", "subjectLabel"],
				helper: "Optionnel. Par défaut « Ma question concerne ». À reformuler quand la page ne reçoit pas des questions, par exemple un signalement."
			},
			{
				type: "text",
				label: "Exemple dans le champ Message",
				path: ["content", "messagePlaceholder"],
				helper: "Optionnel. Par défaut « Voici ma question »."
			},
			{
				type: "object",
				label: "Coordonnées (carte latérale)",
				path: ["content", "contactInfo"],
				fields: [
					{ type: "text", label: "Adresse", path: ["address"] },
					{ type: "text", label: "Téléphone", path: ["phone"] },
					{ type: "text", label: "E-mail", path: ["email"] }
				]
			}
		],
		previewComponents: withPreview(ComponentTypes.ContactForm, {
			subjectOptions: ["Question générale", "Politique monétaire", "Autre"]
		})
	},
	{
		type: ComponentTypes.SeriesExplorer,
		category: "donnees",
		skeleton: { kind: "chart" },
		title: "Explorateur de séries",
		description: "Explorateur interactif de séries de données.",
		tags: ["data", "explorer"],
		defaultContent: {
			series: [
				{ id: "series-1", title: "Series 1", category: "category-1", frequency: "mensuel" },
				{ id: "series-2", title: "Series 2", category: "category-2", frequency: "trimestriel" }
			],
			defaultCategory: "category-1",
			defaultFrequency: "mensuel"
		},
		fields: [
			{
				type: "array",
				label: "Séries",
				itemLabel: "Série",
				itemType: "object",
				defaultItem: { id: "", title: "", category: "", frequency: "mensuel" },
				path: ["content", "series"],
				itemSummaryKeys: ["title", "category"],
				fields: [
					{
						type: "text",
						label: "Identifiant",
						path: ["id"],
						placeholder: "masse-monetaire",
						helper: "Champ technique : identifiant unique de la série."
					},
					{ type: "text", label: "Titre", path: ["title"] },
					{
						type: "text",
						label: "Catégorie",
						path: ["category"],
						helper: "Nom de catégorie utilisé par le filtre de l'explorateur."
					},
					{
						type: "select",
						label: "Fréquence",
						path: ["frequency"],
						options: [
							{ label: "Mensuelle", value: "mensuel" },
							{ label: "Trimestrielle", value: "trimestriel" },
							{ label: "Annuelle", value: "annuel" }
						]
					}
				]
			},
			{
				type: "text",
				label: "Catégorie par défaut",
				path: ["content", "defaultCategory"],
				helper: "« all » pour toutes les catégories, ou le nom exact d'une catégorie."
			},
			{
				type: "select",
				label: "Fréquence par défaut",
				path: ["content", "defaultFrequency"],
				options: [
					{ label: "Toutes", value: "all" },
					{ label: "Mensuelle", value: "mensuel" },
					{ label: "Trimestrielle", value: "trimestriel" },
					{ label: "Annuelle", value: "annuel" }
				]
			},
			{
				type: "text",
				label: "Texte du champ de recherche",
				path: ["content", "searchPlaceholder"],
				helper: "Texte indicatif affiché dans le champ de recherche. Facultatif."
			}
		],
		previewComponents: withPreview(ComponentTypes.SeriesExplorer, {
			series: [
				{ id: "series-1", title: "Series 1", category: "category-1", frequency: "mensuel" },
				{ id: "series-2", title: "Series 2", category: "category-2", frequency: "trimestriel" }
			],
			defaultCategory: "category-1",
			defaultFrequency: "mensuel"
		})
	},
	{
		type: ComponentTypes.KeyMetricsExplorer,
		category: "donnees",
		skeleton: { kind: "chart" },
		title: "Explorateur d'indicateurs",
		description: "Navigateur interactif d'indicateurs clés.",
		tags: ["data", "explorer"],
		defaultContent: {
			metrics: [
				{ id: "metric-1", category: "category-1", label: "Metric 1", value: "0" },
				{ id: "metric-2", category: "category-2", label: "Metric 2", value: "0" }
			],
			defaultCategory: "category-1"
		},
		fields: [
			{
				type: "array",
				label: "Indicateurs",
				itemLabel: "Indicateur",
				itemType: "object",
				defaultItem: { id: "", category: "", label: "", value: "", unit: "" },
				path: ["content", "metrics"],
				itemSummaryKeys: ["label", "value"],
				fields: [
					{
						type: "text",
						label: "Identifiant",
						path: ["id"],
						placeholder: "taux-directeur",
						helper: "Champ technique : identifiant unique de l'indicateur."
					},
					{
						type: "text",
						label: "Catégorie",
						path: ["category"],
						helper: "Nom de catégorie utilisé par le filtre de l'explorateur."
					},
					{ type: "text", label: "Libellé", path: ["label"] },
					{ type: "text", label: "Valeur", path: ["value"], placeholder: "25,0" },
					{ type: "text", label: "Unité", path: ["unit"], placeholder: "%" }
				]
			},
			{
				type: "text",
				label: "Catégorie par défaut",
				path: ["content", "defaultCategory"],
				helper: "Nom exact de la catégorie présélectionnée. Facultatif."
			},
			{
				type: "text",
				label: "Texte du champ de recherche",
				path: ["content", "searchPlaceholder"],
				helper: "Texte indicatif affiché dans le champ de recherche. Facultatif."
			}
		],
		previewComponents: withPreview(ComponentTypes.KeyMetricsExplorer, {
			metrics: [
				{ id: "metric-1", category: "category-1", label: "Metric 1", value: "0" },
				{ id: "metric-2", category: "category-2", label: "Metric 2", value: "0" }
			],
			defaultCategory: "category-1"
		})
	},
	{
		type: ComponentTypes.FxRatesExplorer,
		category: "donnees",
		skeleton: { kind: "chart" },
		title: "Explorateur des taux de change",
		description: "Explorateur des cours de change avec graphiques.",
		tags: ["data", "fx", "explorer"],
		defaultContent: {
			rates: [
				{ code: "CUR1", name: "Currency 1", buy: 0, sell: 0, change1d: 0, change7d: 0 },
				{ code: "CUR2", name: "Currency 2", buy: 0, sell: 0, change1d: 0, change7d: 0 }
			],
			defaultFrom: "CUR1",
			defaultTo: "CUR2"
		},
		fields: [
			{
				type: "select",
				label: "Rôle de la page",
				path: ["content", "variant"],
				options: [
					{ value: "", label: "Convertisseur (défaut)" },
					{ value: "converter", label: "Convertisseur" },
					{ value: "market", label: "Tableau des cours" }
				],
				helper:
					"« Convertisseur » affiche le formulaire de conversion à côté du graphique ; « Tableau des cours » met le graphique en pleine largeur. Combinez avec les sections ci-dessous pour que deux pages ne se répètent pas."
			},
			{
				type: "select",
				label: "Bande analytique",
				path: ["content", "showAnalytics"],
				options: [
					{ value: "", label: "Affichée (défaut)" },
					{ value: "false", label: "Masquée" }
				],
				helper: "Cours sélectionné, variation sur la période, volatilité annualisée, écart acheteur/vendeur."
			},
			{
				type: "select",
				label: "Graphique",
				path: ["content", "showChart"],
				options: [
					{ value: "", label: "Affiché (défaut)" },
					{ value: "false", label: "Masqué" }
				]
			},
			{
				type: "select",
				label: "Plages du graphique",
				path: ["content", "chartRanges"],
				options: [
					{ value: "", label: "1 mois à 1 an (défaut)" },
					{ value: "1m,3m,6m,1y,5y", label: "1 mois à 5 ans" }
				],
				helper:
					"N'ouvrez les 5 ans que sur un module dont l'historique porte réellement cinq ans, sinon le bouton affiche la même courbe que « 1y » sous une autre étiquette."
			},
			{
				type: "select",
				label: "Tableau de toutes les devises",
				path: ["content", "showAllCurrencies"],
				options: [
					{ value: "", label: "Affiché (défaut)" },
					{ value: "false", label: "Masqué" }
				],
				helper:
					"Masquez-le sur une page outil : le renvoi « voir tous les cours » reste affiché et le visiteur retrouve le répertoire complet sur la page des cours."
			},
			{
				type: "text",
				label: "Devise vedette par défaut",
				path: ["content", "defaultActive"],
				placeholder: "USD",
				helper: "Les cours, historiques et drapeaux sont alimentés automatiquement à la publication par le fragment « fx-rates-explorer » : ils ne s'éditent pas ici. Seuls les réglages et libellés ci-dessous se personnalisent."
			},
			{
				type: "text",
				label: "Devise source du convertisseur",
				path: ["content", "defaultFrom"],
				placeholder: "CDF"
			},
			{
				type: "text",
				label: "Devise cible du convertisseur",
				path: ["content", "defaultTo"],
				placeholder: "USD"
			},
			{
				type: "text",
				label: "Sur-titre du cours vedette",
				path: ["content", "heroEyebrow"],
				placeholder: "Cours indicatif du jour",
				helper: "Laisser vide pour le libellé par défaut."
			},
			{
				type: "text",
				label: "Titre du convertisseur",
				path: ["content", "converterTitle"],
				placeholder: "Convertisseur"
			},
			{
				type: "text",
				label: "Légende du convertisseur",
				path: ["content", "converterCaption"],
				placeholder: "Taux indicatifs BCC"
			},
			{
				type: "text",
				label: "Titre du tableau des devises",
				path: ["content", "allCurrenciesTitle"],
				placeholder: "Toutes les devises"
			},
			{
				type: "text",
				label: "Libellé « voir tous les cours »",
				path: ["content", "viewAllLabel"],
				placeholder: "Voir tous les cours"
			},
			{
				type: "text",
				label: "Cible « voir tous les cours »",
				path: ["content", "detailHref"],
				placeholder: "marche-des-changes",
				helper: "Slug de la page de détail des cours. Par défaut : « marche-des-changes »."
			},
			{
				type: "textarea",
				label: "Avertissement",
				path: ["content", "disclaimerText"],
				helper: "Mention affichée sous le tableau. Laisser vide pour l'avis « taux indicatifs » par défaut."
			}
		],
		previewComponents: withPreview(ComponentTypes.FxRatesExplorer, {
			rates: [
				{ code: "CUR1", name: "Currency 1", buy: 0, sell: 0, change1d: 0, change7d: 0 },
				{ code: "CUR2", name: "Currency 2", buy: 0, sell: 0, change1d: 0, change7d: 0 }
			],
			defaultFrom: "CUR1",
			defaultTo: "CUR2"
		})
	},
	{
		type: ComponentTypes.ArticleHero,
		category: "pages-dediees",
		skeleton: { kind: "hero" },
		title: "En-tête d'article",
		description: "En-tête d'article enrichi avec fil d'Ariane et partage.",
		tags: ["article", "hero"],
		defaultContent: {
			eyebrow: "Category",
			breadcrumbs: [],
			breadcrumbsSource: "categories",
			breadcrumbsCategoryPathIds: [],
			breadcrumbsCategoryPath: "",
			breadcrumbsLink: { slug: "" },
			title: "Article title",
			summary: "Article summary",
			author: "",
			authorRole: "",
			publishDate: "01/2025",
			featuredImage: {
				src: DEFAULT_PRERENDER_IMAGE,
				alt: "Image"
			}
		},
		fields: [
			{ type: "text", label: "Eyebrow", path: ["content", "eyebrow"], helper: "Optional label above the title." },
			{
				type: "select",
				label: "Breadcrumbs source",
				path: ["content", "breadcrumbsSource"],
				options: [
					{ label: "Category path", value: "categories" },
					{ label: "Dynamic categories", value: "dynamic" }
				],
				helper: "Category path = select a category path. Dynamic = use dynamic content categories."
			},
			{
				type: "categoryPath",
				label: "Category path",
				path: ["content", "breadcrumbsCategoryPathIds"],
				helper: "Select any category node to auto-build breadcrumbs from its path."
			},
			{
				type: "text",
				label: "Category path (variable)",
				path: ["content", "breadcrumbsCategoryPath"],
				helper: "Optional key path or variable (ex: key1/key2 or {categories}). If set, it overrides the selected category path."
			},
			{
				type: "href",
				label: "Breadcrumbs link",
				path: ["content", "breadcrumbsLink"],
				helper: "Base link for all breadcrumb items. Category path is appended as ?category=key1/key2."
			},
			{ type: "text", label: "Title", path: ["content", "title"] },
			{ type: "textarea", label: "Summary", path: ["content", "summary"] },
			{ type: "text", label: "Publish date", path: ["content", "publishDate"] },
			{
				type: "image",
				label: "Image src",
				path: ["content", "featuredImage", "src"],
				helper: "Select an image from storage (CDN key only)."
			},
			{ type: "text", label: "Image alt", path: ["content", "featuredImage", "alt"] }
		],
		previewComponents: withPreview(ComponentTypes.ArticleHero, {
			eyebrow: "Category",
			title: "Article title",
			summary: "Article summary",
			author: "",
			authorRole: "",
			publishDate: "01/2025",
			featuredImage: {
				src: DEFAULT_PRERENDER_IMAGE,
				alt: "Image"
			}
		})
	},
	{
		type: ComponentTypes.BlogArticleGrid,
		category: "cartes",
		skeleton: { kind: "cards", count: 3, withImage: true },
		title: "Grille d'articles de blog",
		description: "Grille d'articles de blog avec appel a l'action.",
		tags: ["blog", "grid"],
		defaultContent: {
			items: [
				{
					id: "blog-1",
					title: "Blog title 1",
					excerpt: "Blog excerpt 1",
					href: { slug: "/blog/post-1" },
					imageSrc: DEFAULT_PRERENDER_IMAGE
				},
				{
					id: "blog-2",
					title: "Blog title 2",
					excerpt: "Blog excerpt 2",
					href: { slug: "/blog/post-2" },
					imageSrc: DEFAULT_PRERENDER_IMAGE
				}
			],
			ctaLabel: "View blog",
			ctaHref: { slug: "/blog" }
		},
		fields: [
			{
				type: "array",
				label: "Articles",
				itemLabel: "Article",
				itemType: "object",
				defaultItem: { title: "", excerpt: "", href: { slug: "" }, imageSrc: DEFAULT_PRERENDER_IMAGE },
				path: ["content", "items"],
				fields: [
					{ type: "text", label: "Title", path: ["title"] },
					{ type: "textarea", label: "Excerpt", path: ["excerpt"] },
					{
						type: "href",
						label: "Destination",
						path: ["href"],
						helper: "Select a blog page or external URL."
					},
					{
						type: "image",
						label: "Image src",
						path: ["imageSrc"],
						helper: "Select an image from storage (CDN key only)."
					}
				]
			},
			{ type: "text", label: "CTA label", path: ["content", "ctaLabel"] },
			{
				type: "href",
				label: "CTA destination",
				path: ["content", "ctaHref"],
				helper: "Choose the blog index or another target."
			}
		],
		previewComponents: withPreview(ComponentTypes.BlogArticleGrid, {
			items: [
				{
					id: "blog-1",
					title: "Blog title 1",
					excerpt: "Blog excerpt 1",
					href: { slug: "/blog/post-1" },
					imageSrc: DEFAULT_PRERENDER_IMAGE
				},
				{
					id: "blog-2",
					title: "Blog title 2",
					excerpt: "Blog excerpt 2",
					href: { slug: "/blog/post-2" },
					imageSrc: DEFAULT_PRERENDER_IMAGE
				}
			],
			ctaLabel: "View blog",
			ctaHref: { slug: "/blog" }
		})
	},
	{
		type: ComponentTypes.NewsletterSignup,
		category: "formulaires",
		skeleton: { kind: "form" },
		title: "Inscription à l'infolettre",
		description: "Capture d'adresse courriel avec texte de consentement.",
		tags: ["form", "newsletter"],
		defaultContent: {
			title: "Newsletter title",
			description: "Newsletter description",
			consentLabel: "Consent label",
			emailPlaceholder: "Email",
			requiredNote: "Required",
			submitLabel: "Submit"
		},
		fields: [
			{ type: "text", label: "Title", path: ["content", "title"] },
			{ type: "textarea", label: "Description", path: ["content", "description"] },
			{ type: "text", label: "Consent label", path: ["content", "consentLabel"] },
			{ type: "text", label: "Email placeholder", path: ["content", "emailPlaceholder"] },
			{ type: "text", label: "Required note", path: ["content", "requiredNote"] },
			{ type: "text", label: "Submit label", path: ["content", "submitLabel"] }
		],
		previewComponents: withPreview(ComponentTypes.NewsletterSignup, {
			title: "Newsletter title",
			description: "Newsletter description",
			consentLabel: "Consent label",
			emailPlaceholder: "Email",
			requiredNote: "Required",
			submitLabel: "Submit"
		})
	},
	{
		type: ComponentTypes.SingleLink,
		category: "texte",
		primary: true,
		skeleton: { kind: "cta" },
		title: "Lien",
		description: "Bloc de lien texte simple.",
		tags: ["link"],
		defaultContent: {
			href: { slug: "/link" },
			label: "Link label"
		},
		fields: [
			{ type: "text", label: "Label", path: ["content", "label"] },
			{
				type: "href",
				label: "Destination",
				path: ["content", "href"],
				helper: "Select a page, type a path, or paste an external URL."
			}
		],
		previewComponents: withPreview(ComponentTypes.SingleLink, { href: { slug: "/link" }, label: "Link label" })
	},
	{
		type: ComponentTypes.MapEmbed,
		category: "medias",
		skeleton: { kind: "image" },
		title: "Carte Google Maps",
		description: "Carte Google Maps intégrée (URL d'embed officielle uniquement).",
		tags: ["map", "carte", "maps", "adresse"],
		defaultContent: {
			embedUrl: "",
			title: "Carte",
			height: 420
		},
		fields: [
			{
				type: "text",
				label: "URL d'intégration Google Maps",
				path: ["content", "embedUrl"],
				helper: "Sur Google Maps : Partager → « Intégrer une carte », copier l'URL du src de l'iframe (commence par https://www.google.com/maps/embed). Toute autre URL est ignorée."
			},
			{ type: "text", label: "Titre accessible", path: ["content", "title"], helper: "Annoncé aux lecteurs d'écran (défaut : « Carte »)." },
			{ type: "number", label: "Hauteur (px)", path: ["content", "height"], helper: "Entre 200 et 800 : défaut 420." }
		],
		previewComponents: withPreview(ComponentTypes.MapEmbed, {
			embedUrl: "https://www.google.com/maps/embed?pb=exemple",
			title: "Carte",
			height: 420
		})
	},
	{
		type: ComponentTypes.HeroCarousel,
		category: "accueil",
		skeleton: { kind: "hero" },
		title: "Bannière carrousel (accueil)",
		description:
			"Bannière animée « à la une » : 3 à 5 diapositives en fondu (image plein écran, titre, boutons), défilement automatique avec pause.",
		tags: ["hero", "carrousel", "carousel", "bannière", "accueil", "une"],
		defaultContent: {
			autoplaySeconds: 7,
			slides: [
				{
					eyebrow: "Banque Centrale du Congo",
					title: "La Banque Centrale au service des Congolais",
					body: "",
					imageSrc: DEFAULT_PRERENDER_IMAGE,
					imageAlt: "",
					ctaLabel: "Découvrir la Banque",
					ctaHref: { slug: "" },
					secondaryLabel: "",
					secondaryHref: { slug: "" }
				},
				{
					eyebrow: "À la une",
					title: "Titre de l'actualité mise en avant",
					body: "",
					imageSrc: DEFAULT_PRERENDER_IMAGE,
					imageAlt: "",
					ctaLabel: "Lire la suite",
					ctaHref: { slug: "" },
					secondaryLabel: "",
					secondaryHref: { slug: "" }
				}
			]
		},
		fields: [
			{
				type: "number",
				label: "Durée par diapositive (secondes)",
				path: ["content", "autoplaySeconds"],
				helper: "Défaut 7 s. Mettre 0 pour désactiver le défilement automatique."
			},
			{
				type: "array",
				label: "Diapositives",
				itemLabel: "Diapositive",
				itemType: "object",
				defaultItem: {
					eyebrow: "",
					title: "",
					body: "",
					imageSrc: DEFAULT_PRERENDER_IMAGE,
					imageAlt: "",
					ctaLabel: "",
					ctaHref: { slug: "" },
					secondaryLabel: "",
					secondaryHref: { slug: "" }
				},
				path: ["content", "slides"],
				fields: [
					{ type: "text", label: "Surtitre (petite ligne ambre)", path: ["eyebrow"], helper: "Ex. « À la une », « Billets et monnaie »." },
					{ type: "text", label: "Titre", path: ["title"] },
					{ type: "textarea", label: "Texte (1 phrase, coupé à 2 lignes)", path: ["body"] },
					{ type: "image", label: "Image de fond", path: ["imageSrc"], helper: "Image large (≥ 1600 px) ; le texte se pose sur la moitié gauche." },
					{ type: "text", label: "Texte alternatif de l'image", path: ["imageAlt"] },
					{ type: "text", label: "Bouton principal : libellé", path: ["ctaLabel"] },
					{ type: "href", label: "Bouton principal : destination", path: ["ctaHref"] },
					{ type: "text", label: "Lien secondaire : libellé", path: ["secondaryLabel"] },
					{ type: "href", label: "Lien secondaire : destination", path: ["secondaryHref"] }
				]
			}
		],
		previewComponents: withPreview(ComponentTypes.HeroCarousel, {
			autoplaySeconds: 7,
			slides: [
				{
					eyebrow: "À la une",
					title: "La Banque Centrale au service des Congolais",
					body: "Stabilité des prix, confiance dans le Franc Congolais.",
					imageSrc: DEFAULT_PRERENDER_IMAGE,
					ctaLabel: "Découvrir",
					ctaHref: { slug: "" }
				}
			]
		})
	},
	{
		type: ComponentTypes.LegislationList,
		category: "texte",
		skeleton: { kind: "list", rows: 4 },
		title: "Liste de documents",
		description: "Liste de liens soulignés avec icône de téléchargement.",
		tags: ["list", "links", "legislation", "documents"],
		defaultContent: {
			items: [
				{ label: "Loi organique n° 18-027 du 13 décembre 2018", href: { slug: "/docs/loi-18-027" } },
				{ label: "Statuts de la Banque Centrale du Congo", href: { slug: "/docs/statuts" } }
			]
		},
		fields: [
			{
				type: "array",
				label: "Document links",
				itemLabel: "Link",
				itemType: "object",
				defaultItem: { label: "", href: { slug: "" } },
				path: ["content", "items"],
				fields: [
					{ type: "text", label: "Label", path: ["label"] },
					{
						type: "file",
						label: "Pick PDF from storage",
						path: ["href"],
						folder: "documents",
						helper: "Select a PDF from the documents folder. Clicking the label or download icon will open this file."
					},
					{
						type: "href",
						label: "Or destination (page / PDF URL)",
						path: ["href"],
						helper: "Alternatively choose a page or paste a PDF URL. Overwrites the PDF picker if set."
					}
				]
			}
		],
		previewComponents: withPreview(ComponentTypes.LegislationList, {
			items: [
				{ label: "Loi organique n° 18-027 du 13 décembre 2018", href: { slug: "/docs/loi-18-027" } },
				{ label: "Statuts de la Banque Centrale du Congo", href: { slug: "/docs/statuts" } },
				{ label: "Convention entre l'État congolais et la Banque centrale", href: { slug: "/docs/convention" } }
			]
		})
	},
	{
		type: ComponentTypes.NumberedList,
		category: "texte",
		primary: true,
		skeleton: { kind: "list", rows: 3 },
		title: "Liste numérotée",
		description: "Liste ordonnée (1. 2. 3.). Chaque élément est une chaîne.",
		tags: ["list", "text"],
		defaultContent: {
			items: [
				"First item.",
				"Second item.",
				"Third item."
			]
		},
		fields: [
			{
				type: "array",
				label: "List items",
				itemLabel: "Item",
				itemType: "text",
				defaultItem: "",
				path: ["content", "items"]
			}
		],
		previewComponents: withPreview(ComponentTypes.NumberedList, {
			items: [
				"la mise en oeuvre de la politique monétaire de la Banque;",
				"l'octroi des crédits spéciaux visés à l'article 20 de loi organique n°18-027;",
				"les décisions en matière d'agrément des établissements de crédit et autres établissements financiers assujettis au contrôle de la Banque et la prise de toute mesure, sanction, ordre ou décision en vertu des législations de contrôle concernées;",
				"l'édiction des instructions;",
				"l'adoption du budget et les comptes annuels;",
				"les nominations et les promotions du personnel;",
				"l'organisation du régime complémentaire de retraite du personnel."
			]
		})
	},
	{
		type: ComponentTypes.Timeline,
		category: "texte",
		skeleton: { kind: "list", rows: 3 },
		title: "Frise chronologique",
		description: "Chronologie verticale : rail, dates en pastilles bleues, événements sur cartes bleu clair.",
		tags: ["timeline", "frise", "chronologie", "list"],
		defaultContent: {
			items: [
				{ date: "1961", text: "Événement 1" },
				{ date: "1997", text: "Événement 2" }
			]
		},
		fields: [
			{
				type: "array",
				label: "Étapes",
				itemLabel: "Étape",
				itemType: "object",
				defaultItem: { date: "", text: "" },
				path: ["content", "items"],
				fields: [
					{ type: "text", label: "Date", path: ["date"] },
					{ type: "text", label: "Événement", path: ["text"] }
				]
			}
		],
		previewComponents: withPreview(ComponentTypes.Timeline, {
			items: [
				{ date: "30 juillet 1951", text: "Création de la BCCBRU" },
				{ date: "23 février 1961", text: "Création de la Banque Nationale du Congo" },
				{ date: "1997", text: "Retour à la Banque Centrale du Congo" }
			]
		})
	},
	{
		type: ComponentTypes.TwoColumnList,
		category: "texte",
		skeleton: { kind: "list", rows: 3 },
		title: "Liste à deux colonnes",
		description: "Liste de lignes : texte à gauche (bleu) et texte à droite (bleu clair).",
		tags: ["list", "text"],
		defaultContent: {
			items: [
				{ left: "Left 1", right: "Right 1" },
				{ left: "Left 2", right: "Right 2" }
			]
		},
		fields: [
			{
				type: "array",
				label: "Rows",
				itemLabel: "Row",
				itemType: "object",
				defaultItem: { left: "", right: "" },
				path: ["content", "items"],
				fields: [
					{ type: "text", label: "Left text", path: ["left"] },
					{ type: "text", label: "Right text", path: ["right"] }
				]
			}
		],
		previewComponents: withPreview(ComponentTypes.TwoColumnList, {
			items: [
				{ left: "Jeudi 1er janvier", right: "Nouvel an" },
				{ left: "Dimanche 4 janvier", right: "Journée des Martyrs" },
				{ left: "Vendredi 16 janvier", right: "Assassinat de Laurent-Désiré Kabila" },
				{ left: "Samedi 17 janvier", right: "Assassinat de Patrice Lumumba" },
				{ left: "Lundi 6 avril", right: "Kimbangu Day" },
				{ left: "Vendredi 1er mai", right: "Fête du Travail" },
				{ left: "Dimanche 17 mai", right: "Journée de la Libération / Forces armées" },
				{ left: "Mardi 30 juin", right: "Jour de l'Indépendance" },
				{ left: "Samedi 1er août", right: "Journée des Parents" },
				{ left: "Vendredi 25 décembre", right: "Noël" }
			]
		})
	},
	// =====================================================================
	// MarketsBoard : three sub-cards: Exchange / Inflation / Interest.
	// All three are manually authored today; only `inflation` exposes a
	// manual ↔ dynamic mode toggle that mirrors the NewsSection pattern.
	// `exchange` and `interest` keep the same nested shape so adding their
	// own dynamic modes later is purely additive (no schema migration).
	// Live preview for inflation dynamic mode is wired in
	// `useDynamicContentResolvers.resolveMarketsBoardDynamicContent`.
	// =====================================================================
	{
		type: ComponentTypes.MarketsBoard,
		category: "donnees",
		skeleton: { kind: "ticker" },
		title: "Tableau des marchés",
		description: "Panneau à trois volets : cours de change, taux d'inflation, taux d'intérêt.",
		tags: ["home", "markets", "indicators", "dynamic"],
		defaultContent: {
			title: "Marchés et indicateurs",
			subtitle: "Données clés mises à jour quotidiennement par la Banque Centrale du Congo.",
			actionLabel: "Convertisseur de taux",
			actionHref: { slug: "/chiffres-cles" },
			exchange: {
				title: "Cours de change",
				asOf: "",
				featured: { label: "1 USD = 0,0000 CDF", delta1d: "", delta2d: "", caption: "cours indicatif", trend: "flat" },
				rows: [],
				sparkline: [],
				moreLabel: "Plus de détails",
				moreHref: { slug: "/cours-de-change" }
			},
			inflation: {
				title: "Taux d'inflation",
				unitCaption: "(valeurs en %)",
				mode: "manual",
				// Default mapping mirrors the canonical key names so the
				// dropdowns show real values right away when an editor
				// switches to dynamic mode (rather than a sea of "None"s).
				// The projector treats empty entries as "use the default
				// key" anyway, so persisted documents that never had
				// mapping keep working.
				dynamicComponent: {
					contentTypeKey: "",
					sortField: "date",
					fieldMapping: {
						date: "date",
						source: "source",
						hebdomadaire: "hebdomadaire",
						"cumule-annee": "cumule-annee",
						"glissement-annuel": "glissement-annuel",
						annualise: "annualise"
					},
					sparklineField: "annualise",
					sparklineWindow: 8
				},
				asOf: "",
				source: "INS",
				rows: [],
				sparkline: [],
				moreLabel: "Tous les indicateurs",
				moreHref: { slug: "/statistiques" }
			},
			interest: {
				title: "Taux d'intérêt",
				directeur: { title: "Taux Directeur", asOfLabel: "Depuis le", asOf: "", rows: [] },
				bons: { title: "Bons BCC", asOfLabel: "Du", asOf: "", columns: { jours: "JOURS", tmp: "TMP", tm: "TM" }, rows: [] },
				moreLabel: "Tous les taux",
				moreHref: { slug: "/taux-interet" }
			}
		},
		fields: [
			{ type: "text", label: "Title", path: ["content", "title"] },
			{ type: "textarea", label: "Subtitle", path: ["content", "subtitle"] },
			{ type: "text", label: "Action label", path: ["content", "actionLabel"] },
			{ type: "href", label: "Action link", path: ["content", "actionHref"] },

			// ---- Cours de change (manual only for now; structure kept identical to inflation
			//      so adding mode toggle later is a one-line change) ----
			{
				type: "object",
				label: "Cours de change",
				path: ["content", "exchange"],
				fields: [
					{ type: "text", label: "Title", path: ["title"] },
					{ type: "text", label: "As-of", path: ["asOf"], helper: "Free text date/time stamp shown in the card header." },
					{
						type: "object",
						label: "Featured",
						path: ["featured"],
						fields: [
							{ type: "text", label: "Label", path: ["label"] },
							{ type: "text", label: "Delta 1 day", path: ["delta1d"] },
							{ type: "text", label: "Delta 2 days", path: ["delta2d"] },
							{ type: "text", label: "Caption", path: ["caption"] },
							{
								type: "select",
								label: "Trend",
								path: ["trend"],
								options: [
									{ label: "Up", value: "up" },
									{ label: "Down", value: "down" },
									{ label: "Flat", value: "flat" }
								]
							}
						]
					},
					{
						type: "array",
						label: "Currency rows",
						itemLabel: "Row",
						itemType: "object",
						defaultItem: { code: "", value: "", flag: "", trend: "flat" },
						path: ["rows"],
						fields: [
							{ type: "text", label: "Code", path: ["code"], helper: "ISO 4217 (USD, EUR, …)" },
							{ type: "text", label: "Value", path: ["value"] },
							{ type: "text", label: "Flag (ISO-2)", path: ["flag"], helper: "Lowercase 2-letter (us, eu, gb, cn)" },
							{
								type: "select",
								label: "Trend",
								path: ["trend"],
								options: [
									{ label: "Up", value: "up" },
									{ label: "Down", value: "down" },
									{ label: "Flat", value: "flat" }
								]
							}
						]
					},
					{ type: "text", label: "More label", path: ["moreLabel"] },
					{ type: "href", label: "More link", path: ["moreHref"] }
				]
			},

			// ---- Taux d'inflation (manual ↔ dynamic) ----
			{
				type: "object",
				label: "Taux d'inflation",
				path: ["content", "inflation"],
				fields: [
					{ type: "text", label: "Title", path: ["title"] },
					{ type: "text", label: "Unit caption", path: ["unitCaption"] },
					{
						type: "select",
						label: "Mode",
						path: ["mode"],
						options: [
							{ label: "Manual", value: "manual" },
							{ label: "Dynamic", value: "dynamic" }
						],
						helper: "Manual uses the rows below. Dynamic pulls the latest record of a content type at deploy time."
					},
					{
						type: "object",
						label: "Dynamic settings",
						path: ["dynamicComponent"],
						showWhen: { path: ["content", "inflation", "mode"], equals: "dynamic" },
						fields: [
							{
								type: "contentTypeSelect",
								label: "Content type",
								path: ["contentTypeKey"],
								helper: "Select the content type whose latest record drives the card."
							},
							{
								type: "contentFieldSelect",
								label: "Sort field (date)",
								path: ["sortField"],
								contentTypePath: ["content", "inflation", "dynamicComponent", "contentTypeKey"],
								fieldTypes: ["date"],
								includeBaseFields: false,
								allowEmpty: true,
								valueFormat: "raw",
								helper: "Newest record by this field becomes the displayed snapshot."
							},
							{
								type: "object",
								label: "Field mapping",
								path: ["fieldMapping"],
								helper: "Override which fields supply each row. Empty = use the default key.",
								fields: [
									{ type: "contentFieldSelect", label: "Date field",        path: ["date"],              contentTypePath: ["content", "inflation", "dynamicComponent", "contentTypeKey"], includeBaseFields: false, allowEmpty: true, valueFormat: "raw" },
									{ type: "contentFieldSelect", label: "Source field",      path: ["source"],            contentTypePath: ["content", "inflation", "dynamicComponent", "contentTypeKey"], includeBaseFields: false, allowEmpty: true, valueFormat: "raw" },
									{ type: "contentFieldSelect", label: "Hebdomadaire",      path: ["hebdomadaire"],      contentTypePath: ["content", "inflation", "dynamicComponent", "contentTypeKey"], includeBaseFields: false, allowEmpty: true, valueFormat: "raw" },
									{ type: "contentFieldSelect", label: "Cumulé année",      path: ["cumule-annee"],      contentTypePath: ["content", "inflation", "dynamicComponent", "contentTypeKey"], includeBaseFields: false, allowEmpty: true, valueFormat: "raw" },
									{ type: "contentFieldSelect", label: "Glissement annuel", path: ["glissement-annuel"], contentTypePath: ["content", "inflation", "dynamicComponent", "contentTypeKey"], includeBaseFields: false, allowEmpty: true, valueFormat: "raw" },
									{ type: "contentFieldSelect", label: "Annualisé",         path: ["annualise"],         contentTypePath: ["content", "inflation", "dynamicComponent", "contentTypeKey"], includeBaseFields: false, allowEmpty: true, valueFormat: "raw" }
								]
							},
							// Sparkline projection : author picks one numeric
							// field; the deploy projector plots its last N
							// values (oldest → newest). Replaces the
							// previously-hardcoded fake series.
							{
								type: "contentFieldSelect",
								label: "Sparkline field",
								path: ["sparklineField"],
								contentTypePath: ["content", "inflation", "dynamicComponent", "contentTypeKey"],
								includeBaseFields: false,
								allowEmpty: true,
								valueFormat: "raw",
								helper: "Numeric field to chart (last N records). Empty = no sparkline."
							},
							{
								type: "number",
								label: "Sparkline window",
								path: ["sparklineWindow"],
								helper: "How many recent records the sparkline plots (default 8)."
							}
						]
					},
					{
						type: "text",
						label: "As-of",
						path: ["asOf"],
						showWhen: { path: ["content", "inflation", "mode"], equals: "manual" }
					},
					{
						type: "text",
						label: "Source",
						path: ["source"],
						showWhen: { path: ["content", "inflation", "mode"], equals: "manual" }
					},
					{
						type: "array",
						label: "Rows",
						itemLabel: "Row",
						itemType: "object",
						defaultItem: { label: "", value: "" },
						path: ["rows"],
						showWhen: { path: ["content", "inflation", "mode"], equals: "manual" },
						fields: [
							{ type: "text", label: "Label", path: ["label"] },
							{ type: "text", label: "Value", path: ["value"] }
						]
					},
					{ type: "text", label: "More label", path: ["moreLabel"] },
					{ type: "href", label: "More link", path: ["moreHref"] }
				]
			},

			// ---- Taux d'intérêt (manual only for now; same pattern as inflation
			//      to make adding dynamic mode later a one-line addition) ----
			{
				type: "object",
				label: "Taux d'intérêt",
				path: ["content", "interest"],
				fields: [
					{ type: "text", label: "Title", path: ["title"] },
					{
						type: "object",
						label: "Taux Directeur",
						path: ["directeur"],
						fields: [
							{ type: "text", label: "Title", path: ["title"] },
							{ type: "text", label: "As-of label", path: ["asOfLabel"], helper: "Prefix shown next to the date (e.g. \"Depuis le\")." },
							{ type: "text", label: "As-of", path: ["asOf"] },
							{
								type: "array",
								label: "Rows",
								itemLabel: "Row",
								itemType: "object",
								defaultItem: { label: "", value: "", trend: "flat" },
								path: ["rows"],
								fields: [
									{ type: "text", label: "Label", path: ["label"] },
									{ type: "text", label: "Value", path: ["value"] },
									{
										type: "select",
										label: "Trend",
										path: ["trend"],
										options: [
											{ label: "Up", value: "up" },
											{ label: "Down", value: "down" },
											{ label: "Flat", value: "flat" }
										]
									}
								]
							}
						]
					},
					{
						type: "object",
						label: "Bons BCC",
						path: ["bons"],
						fields: [
							{ type: "text", label: "Title", path: ["title"] },
							{ type: "text", label: "As-of label", path: ["asOfLabel"] },
							{ type: "text", label: "As-of", path: ["asOf"] },
							{
								type: "object",
								label: "Column headers",
								path: ["columns"],
								fields: [
									{ type: "text", label: "Jours", path: ["jours"] },
									{ type: "text", label: "TMP",   path: ["tmp"] },
									{ type: "text", label: "TM",    path: ["tm"] }
								]
							},
							{
								type: "array",
								label: "Rows",
								itemLabel: "Row",
								itemType: "object",
								defaultItem: { jours: "", tmp: "", tm: "" },
								path: ["rows"],
								fields: [
									{ type: "text", label: "Jours", path: ["jours"] },
									{ type: "text", label: "TMP",   path: ["tmp"] },
									{ type: "text", label: "TM",    path: ["tm"] }
								]
							}
						]
					},
					{ type: "text", label: "More label", path: ["moreLabel"] },
					{ type: "href", label: "More link", path: ["moreHref"] }
				]
			}
		],
		previewComponents: withPreview(ComponentTypes.MarketsBoard, {
			title: "Marchés et indicateurs",
			subtitle: "Données clés mises à jour quotidiennement par la Banque Centrale du Congo.",
			actionLabel: "Convertisseur de taux",
			actionHref: { slug: "/chiffres-cles" },
			exchange: {
				title: "Cours de change",
				asOf: "-",
				featured: { label: "1 USD = 0,0000 CDF", delta1d: "0,00 % · 1jr", delta2d: "0,00 % · 2jrs", caption: "cours indicatif", trend: "flat" },
				rows: [
					{ code: "USD", value: "0,0000", flag: "us", trend: "flat" },
					{ code: "EUR", value: "0,0000", flag: "eu", trend: "flat" }
				],
				sparkline: [],
				moreLabel: "Plus de détails",
				moreHref: { slug: "/cours-de-change" }
			},
			inflation: {
				title: "Taux d'inflation",
				unitCaption: "(valeurs en %)",
				mode: "manual",
				asOf: "-",
				source: "INS",
				rows: [
					{ label: "Hebdomadaire", value: "0,000" },
					{ label: "Cumulé de l'année", value: "0,000" },
					{ label: "Glissement annuel", value: "0,000" },
					{ label: "Annualisé", value: "0,000" }
				],
				moreLabel: "Tous les indicateurs",
				moreHref: { slug: "/statistiques" }
			},
			interest: {
				title: "Taux d'intérêt",
				directeur: { title: "Taux Directeur", asOfLabel: "Depuis le", asOf: "-", rows: [] },
				bons: { title: "Bons BCC", asOfLabel: "Du", asOf: "-", columns: { jours: "JOURS", tmp: "TMP", tm: "TM" }, rows: [] },
				moreLabel: "Tous les taux",
				moreHref: { slug: "/taux-interet" }
			}
		})
	},
	// =====================================================================
	// DataRecordDetail : generic detail block for one dated data record.
	// Authored once as a dynamic-path template page: typeLabel is static,
	// recordDate / each field value / pdfKey / body carry `{token}`
	// placeholders the deploy engine substitutes per record. Unsubstituted
	// `{token}` values are skipped by the renderer at display time.
	// =====================================================================
	{
		type: ComponentTypes.DataRecordDetail,
		category: "pages-dediees",
		hidden: true,
		skeleton: { kind: "table" },
		title: "Détail d'un enregistrement",
		description: "Bloc de detail pour un enregistrement daté (en-tête, tableau, PDF, corps).",
		tags: ["data", "record", "dynamic", "template"],
		defaultContent: {
			typeLabel: "Cours de change",
			recordDate: "{date}",
			fields: [
				{ label: "Libellé", value: "{valeur}" }
			],
			pdfKey: "",
			body: "",
			sourceLabel: ""
		},
		fields: [
			{
				type: "text",
				label: "Type label",
				path: ["content", "typeLabel"],
				helper: "Static content-type label shown in the header band (e.g. \"Cours de change\")."
			},
			{
				type: "text",
				label: "Record date",
				path: ["content", "recordDate"],
				helper: "Display date. In a template use a token, e.g. {date}."
			},
			{
				type: "array",
				label: "Fields",
				itemLabel: "Field",
				itemType: "object",
				defaultItem: { label: "", value: "" },
				path: ["content", "fields"],
				fields: [
					{ type: "text", label: "Label", path: ["label"] },
					{ type: "text", label: "Value", path: ["value"], helper: "In a template use a token, e.g. {valeur}." }
				]
			},
			{
				type: "text",
				label: "PDF key",
				path: ["content", "pdfKey"],
				helper: "Storage key of an attached PDF. In a template use a token, e.g. {fichier}. Leave empty for none."
			},
			{
				type: "textarea",
				label: "Body",
				path: ["content", "body"],
				helper: "Optional rich-text body. In a template use a token, e.g. {body}."
			},
			{
				type: "text",
				label: "Source label",
				path: ["content", "sourceLabel"],
				helper: "Optional footer note (e.g. \"Source : INS\")."
			}
		],
		previewComponents: withPreview(ComponentTypes.DataRecordDetail, {
			typeLabel: "Cours de change",
			recordDate: "2 janvier 2024",
			fields: [
				{ label: "USD", value: "2 500,00" },
				{ label: "EUR", value: "2 750,50" }
			],
			pdfKey: "",
			body: "",
			sourceLabel: "Source : Banque Centrale du Congo"
		})
	},
	// =====================================================================
	// RichText : renders editorial body HTML faithfully (headings, lists,
	// links, images, quotes, tables). Used for migrated legacy editorial
	// pages where the Paragraph block's sanitiser would strip the markup.
	// =====================================================================
	{
		type: ComponentTypes.RichText,
		category: "texte",
		skeleton: { kind: "paragraph" },
		title: "Texte enrichi",
		description: "Rend fidèlement le HTML éditorial : titres, listes, liens, images, citations, tableaux.",
		tags: ["text", "content", "html", "editorial"],
		defaultContent: {
			html: "<h2>Titre</h2><p>Contenu de la page.</p>"
		},
		fields: [
			{
				type: "richtext",
				label: "Corps du texte",
				path: ["content", "html"],
				helper: "Titres, listes, liens, icônes… Le HTML est assaini au rendu (scripts et attributs dangereux supprimés)."
			}
		],
		previewComponents: withPreview(ComponentTypes.RichText, {
			html: "<h2>Titre</h2><p>Contenu de la page.</p><ul><li>Premier élément</li><li>Deuxième élément</li></ul>"
		})
	},
	// =====================================================================
	// Gallery : a responsive captioned image grid. Used by the migrated
	// legacy "Gallérie des photos" pages and banknote galleries.
	// =====================================================================
	{
		type: ComponentTypes.Gallery,
		category: "medias",
		primary: true,
		skeleton: { kind: "gallery", count: 4 },
		title: "Galerie",
		description: "Grille d'images légendées et responsive pour photos et galeries de billets.",
		tags: ["image", "gallery", "media", "editorial"],
		defaultContent: {
			images: [{ src: "", caption: "" }]
		},
		fields: [
			{
				type: "number",
				label: "Columns",
				path: ["content", "columns"],
				helper: "Optional column count (1-4). Leave empty for a responsive layout."
			},
			{
				type: "array",
				label: "Images",
				itemLabel: "Image",
				itemType: "object",
				defaultItem: { src: "", caption: "" },
				path: ["content", "images"],
				fields: [
					{
						type: "image",
						label: "Image",
						path: ["src"],
						helper: "Pick an image from storage (CDN key only)."
					},
					{ type: "text", label: "Caption", path: ["caption"] }
				]
			}
		],
		previewComponents: withPreview(ComponentTypes.Gallery, {
			images: [
				{ src: DEFAULT_PRERENDER_IMAGE, caption: "Première photo" },
				{ src: DEFAULT_PRERENDER_IMAGE, caption: "Deuxième photo" },
				{ src: DEFAULT_PRERENDER_IMAGE, caption: "Troisième photo" }
			]
		})
	},
	// =====================================================================
	// SpecimenMarquee : bande défilante de spécimens. Vitrine des documents de
	// sécurité de l'Hôtel des Monnaies : défilement lent, arrêt au survol et au
	// focus clavier, pastille « Spécimen » sur chaque visuel.
	// =====================================================================
	{
		type: ComponentTypes.SpecimenMarquee,
		category: "medias",
		skeleton: { kind: "gallery", count: 4 },
		title: "Bande de spécimens",
		description:
			"Vitrine défilante de documents de sécurité. Le défilement s'arrête au survol et au focus clavier.",
		tags: ["image", "specimen", "media", "hotel des monnaies"],
		defaultContent: {
			title: "",
			items: [{ src: "", caption: "" }]
		},
		fields: [
			{ type: "text", label: "Titre de section", path: ["content", "title"] },
			{
				type: "text",
				label: "Texte de la pastille",
				path: ["content", "specimenLabel"],
				helper: "« Spécimen » par défaut."
			},
			{
				type: "array",
				label: "Visuels",
				itemLabel: "Visuel",
				itemType: "object",
				defaultItem: { src: "", caption: "" },
				path: ["content", "items"],
				fields: [
					{
						type: "image",
						label: "Image",
						path: ["src"],
						helper: "Choisir une image du stockage (clé CDN)."
					},
					{ type: "text", label: "Légende", path: ["caption"] }
				]
			}
		],
		previewComponents: withPreview(ComponentTypes.SpecimenMarquee, {
			title: "Nos spécimens",
			items: [
				{ src: DEFAULT_PRERENDER_IMAGE, caption: "Chèque bancaire" },
				{ src: DEFAULT_PRERENDER_IMAGE, caption: "Diplôme d'études" },
				{ src: DEFAULT_PRERENDER_IMAGE, caption: "Vignette fiscale" },
				{ src: DEFAULT_PRERENDER_IMAGE, caption: "Permis de conduire" }
			]
		})
	},
	// =====================================================================
	// SmartCardGrid : grille de cartes adaptatives : chaque carte adapte sa
	// mise en page aux propriétés remplies (image, lien, étiquette…).
	// =====================================================================
	{
		type: ComponentTypes.SmartCardGrid,
		category: "cartes",
		primary: true,
		skeleton: { kind: "cards", count: 3, withImage: true },
		title: "Grille de cartes",
		description:
			"Cartes adaptatives : image, titre, description, lien, étiquette : chaque carte s'adapte aux propriétés remplies.",
		tags: ["grid", "cards", "card"],
		defaultContent: {
			title: "",
			columns: 3,
			cards: [
				{ title: "Première carte", description: "Description de la carte.", linkLabel: "" },
				{ title: "Deuxième carte", description: "Description de la carte.", linkLabel: "" }
			]
		},
		fields: [
			{ type: "text", label: "Titre de la section", path: ["content", "title"], helper: "Facultatif : affiché au-dessus de la grille." },
			{
				type: "select",
				label: "Colonnes (bureau)",
				path: ["content", "columns"],
				options: [
					{ label: "2 colonnes", value: "2" },
					{ label: "3 colonnes", value: "3" },
					{ label: "4 colonnes", value: "4" }
				],
				helper: "Nombre de cartes par ligne sur grand écran."
			},
			{
				type: "array",
				label: "Cartes",
				path: ["content", "cards"],
				itemType: "object",
				itemLabel: "Carte",
				itemSummaryKeys: ["title", "description"],
				defaultItem: { title: "", description: "" },
				fields: [
					{ type: "text", label: "Titre", path: ["title"] },
					{ type: "textarea", label: "Description", path: ["description"] },
					{ type: "image", label: "Image", path: ["image"] },
					{ type: "text", label: "Texte alternatif de l'image", path: ["imageAlt"] },
					{ type: "href", label: "Lien", path: ["href"] },
					{ type: "text", label: "Libellé du lien", path: ["linkLabel"], helper: "Par défaut : « En savoir plus »." },
					{ type: "text", label: "Étiquette", path: ["tag"], helper: "Petite pastille au-dessus du titre (facultatif)." }
				]
			}
		],
		previewComponents: withPreview(ComponentTypes.SmartCardGrid, {
			title: "Nos publications",
			columns: 3,
			cards: [
				{ title: "Rapport annuel", description: "Le rapport annuel de la banque.", image: DEFAULT_PRERENDER_IMAGE, tag: "Publication" },
				{ title: "Statistiques", description: "Les dernières statistiques monétaires." },
				{ title: "Communiqués", description: "Tous les communiqués de presse.", linkLabel: "Consulter" }
			]
		})
	},
	// =====================================================================
	// CtaButton : bouton d'appel à l'action autonome.
	// =====================================================================
	{
		type: ComponentTypes.CtaButton,
		category: "texte",
		skeleton: { kind: "cta" },
		title: "Bouton",
		description: "Un bouton d'appel à l'action autonome : trois styles, alignement au choix.",
		tags: ["cta", "link"],
		defaultContent: { label: "En savoir plus", style: "primary", align: "left" },
		fields: [
			{ type: "text", label: "Libellé", path: ["content", "label"] },
			{ type: "href", label: "Lien", path: ["content", "href"] },
			{
				type: "select",
				label: "Style",
				path: ["content", "style"],
				options: [
					{ label: "Principal (bleu)", value: "primary" },
					{ label: "Secondaire (contour)", value: "secondary" },
					{ label: "Discret (souligné)", value: "ghost" }
				]
			},
			{
				type: "select",
				label: "Alignement",
				path: ["content", "align"],
				options: [
					{ label: "Gauche", value: "left" },
					{ label: "Centre", value: "center" },
					{ label: "Droite", value: "right" }
				]
			}
		],
		previewComponents: withPreview(ComponentTypes.CtaButton, {
			label: "Consulter le rapport",
			style: "primary",
			align: "left"
		})
	},
	// =====================================================================
	// Quote : citation en exergue avec attribution.
	// =====================================================================
	{
		type: ComponentTypes.Quote,
		category: "texte",
		skeleton: { kind: "quote" },
		title: "Citation",
		description: "Citation en exergue : barre d'accent, texte de la citation, auteur et fonction.",
		tags: ["text", "editorial"],
		defaultContent: { quote: "", author: "", role: "" },
		fields: [
			{ type: "textarea", label: "Citation", path: ["content", "quote"] },
			{ type: "text", label: "Auteur", path: ["content", "author"] },
			{ type: "text", label: "Fonction", path: ["content", "role"] }
		],
		previewComponents: withPreview(ComponentTypes.Quote, {
			quote: "La stabilité monétaire est le socle du développement économique.",
			author: "Direction générale",
			role: "Banque Centrale du Congo"
		})
	},
	// =====================================================================
	// Divider : séparateur visible entre les blocs (complète l'espaceur).
	// =====================================================================
	{
		type: ComponentTypes.Divider,
		category: "mise-en-page",
		skeleton: { kind: "columns", spans: [12] },
		title: "Séparateur",
		description: "Ligne ou points de séparation entre deux blocs, pleine largeur ou centré court.",
		tags: ["layout", "spacing"],
		defaultContent: { variant: "ligne", width: "full" },
		fields: [
			{
				type: "select",
				label: "Style",
				path: ["content", "variant"],
				options: [
					{ label: "Ligne", value: "ligne" },
					{ label: "Points", value: "points" }
				]
			},
			{
				type: "select",
				label: "Largeur",
				path: ["content", "width"],
				options: [
					{ label: "Pleine largeur", value: "full" },
					{ label: "Courte (centrée)", value: "short" }
				]
			}
		],
		previewComponents: withPreview(ComponentTypes.Divider, { variant: "ligne", width: "full" })
	},
	// =====================================================================
	// LayoutSection : grille 12 colonnes responsive : N composants enfants,
	// chaque colonne avec une largeur par point de rupture (en cascade
	// descendante) et une visibilité par écran. Éditeur dédié (fields: []).
	// =====================================================================
	{
		type: ComponentTypes.LayoutSection,
		category: "mise-en-page",
		primary: true,
		skeleton: { kind: "columns", spans: [8, 4] },
		title: "Container",
		description:
			"Regroupe plusieurs composants sur une grille responsive : largeur de chaque colonne réglable par taille d'écran, avec héritage vers les écrans plus petits.",
		tags: ["layout"],
		defaultContent: {
			children: [],
			gap: "md",
			align: "start"
		},
		fields: [],
		previewComponents: withPreview(ComponentTypes.LayoutSection, {
			gap: "md",
			align: "start",
			children: [
				{
					key: "col-1",
					componentType: ComponentTypes.TextBlock,
					span: { lg: 8 },
					content: {
						paragraphs: [
							"La Banque Centrale du Congo veille à la stabilité du cadre macroéconomique et financier.",
							"Ce texte occupe deux tiers de la largeur sur grand écran, pleine largeur sur mobile."
						]
					}
				},
				{
					key: "col-2",
					componentType: ComponentTypes.FullWidthImage,
					span: { lg: 4 },
					content: {
						src: DEFAULT_PRERENDER_IMAGE,
						alt: "Illustration de la section",
						showLoadingCircle: false
					}
				}
			]
		})
	},
	// =====================================================================
	// VideoEmbed : lecteur vidéo 16:9 responsive pour une vidéo unique
	// (YouTube, Vimeo ou toute URL d'intégration). Utilisé par le type de
	// contenu « videos » (vidéos institutionnelles).
	// =====================================================================
	{
		type: ComponentTypes.VideoEmbed,
		category: "medias",
		skeleton: { kind: "video" },
		title: "Vidéo intégrée",
		description: "Lecteur vidéo 16:9 responsive pour une vidéo YouTube, Vimeo ou tout lien d'intégration.",
		tags: ["media", "video", "editorial"],
		defaultContent: {
			title: "Titre de la vidéo",
			embedUrl: ""
		},
		fields: [
			{
				type: "text",
				label: "Titre",
				path: ["content", "title"],
				helper: "Affiché au-dessus du lecteur. Laisser vide pour n'afficher que la vidéo."
			},
			{
				type: "url",
				label: "URL de la vidéo",
				path: ["content", "embedUrl"],
				placeholder: "https://www.youtube.com/watch?v=…",
				helper: "Lien YouTube/Vimeo ou URL d'intégration ; converti automatiquement en lecteur au rendu."
			}
		],
		previewComponents: withPreview(ComponentTypes.VideoEmbed, {
			title: "Allocution du Gouverneur",
			embedUrl: "https://www.youtube.com/embed/exemple-video"
		})
	},
	// =====================================================================
	// BilletTabs : sélecteur rapide entre les pages de coupures (billets).
	// L'onglet actif est rendu en pastille pleine, sans lien.
	// =====================================================================
	{
		type: ComponentTypes.BilletTabs,
		category: "pages-dediees",
		hidden: true,
		skeleton: { kind: "ticker" },
		title: "Onglets des coupures",
		description: "Sélecteur rapide entre les pages des coupures : une pastille par dénomination, l'onglet de la page courante est mis en avant.",
		tags: ["links", "layout"],
		defaultContent: {
			tabs: [
				{ label: "500 FC", href: { slug: "" } },
				{ label: "1 000 FC", href: { slug: "" } }
			],
			activeIndex: 0
		},
		fields: [
			{
				type: "array",
				label: "Onglets",
				itemLabel: "Onglet",
				itemType: "object",
				defaultItem: { label: "", href: { slug: "" } },
				path: ["content", "tabs"],
				itemSummaryKeys: ["label"],
				fields: [
					{ type: "text", label: "Libellé", path: ["label"], placeholder: "500 FC" },
					{
						type: "href",
						label: "Destination",
						path: ["href"],
						helper: "Page de détail de la coupure correspondante."
					}
				]
			},
			{
				type: "number",
				label: "Onglet actif",
				path: ["content", "activeIndex"],
				helper: "Index de l'onglet actif (0 = premier). Il est affiché en pastille pleine, sans lien."
			}
		],
		previewComponents: withPreview(ComponentTypes.BilletTabs, {
			tabs: [
				{ label: "500 FC", href: { slug: "/la-banque/billets-et-monnaie/billet-de-500-francs" } },
				{ label: "1 000 FC", href: { slug: "/la-banque/billets-et-monnaie/billet-de-1000-francs" } },
				{ label: "5 000 FC", href: { slug: "/la-banque/billets-et-monnaie/billet-de-5000-francs" } },
				{ label: "10 000 FC", href: { slug: "/la-banque/billets-et-monnaie/billet-de-10000-francs" } },
				{ label: "20 000 FC", href: { slug: "/la-banque/billets-et-monnaie/billet-de-20000-francs" } }
			],
			activeIndex: 0
		})
	},
	// =====================================================================
	// BilletSecurity : grille numérotée des éléments de sécurité d'une
	// coupure. Les numéros renvoient aux repères imprimés sur la photo du
	// billet affichée plus haut.
	// =====================================================================
	{
		type: ComponentTypes.BilletSecurity,
		category: "pages-dediees",
		hidden: true,
		skeleton: { kind: "cards", count: 3 },
		title: "Éléments de sécurité du billet",
		description: "Grille numérotée des éléments de sécurité d'une coupure ; les numéros renvoient aux repères de la photo du billet.",
		tags: ["grid", "text", "content"],
		defaultContent: {
			title: "Éléments de sécurité",
			features: [
				{ number: 1, title: "Filigrane", description: "Portrait visible par transparence." },
				{ number: 2, title: "Fil de sécurité", description: "Bande métallisée insérée dans le papier." }
			]
		},
		fields: [
			{
				type: "text",
				label: "Titre",
				path: ["content", "title"],
				helper: "Titre de la section. Laisser vide pour le masquer."
			},
			{
				type: "array",
				label: "Éléments de sécurité",
				itemLabel: "Élément",
				itemType: "object",
				defaultItem: { number: 1, title: "", description: "" },
				path: ["content", "features"],
				itemSummaryKeys: ["title", "description"],
				fields: [
					{
						type: "number",
						label: "Numéro",
						path: ["number"],
						helper: "Correspond au repère imprimé sur la photo du billet."
					},
					{ type: "text", label: "Titre", path: ["title"] },
					{ type: "textarea", label: "Description", path: ["description"] }
				]
			}
		],
		previewComponents: withPreview(ComponentTypes.BilletSecurity, {
			title: "Éléments de sécurité",
			features: [
				{ number: 1, title: "Filigrane", description: "Portrait visible par transparence, identique au motif principal." },
				{ number: 2, title: "Fil de sécurité", description: "Bande métallisée insérée dans l'épaisseur du papier." },
				{ number: 3, title: "Encre à couleur changeante", description: "La valeur faciale change de couleur selon l'angle." },
				{ number: 4, title: "Impression en relief", description: "Motifs perceptibles au toucher sur le recto." }
			]
		})
	},
	// =====================================================================
	// TextWithFigure : bloc éditorial à deux colonnes : intro mise en
	// exergue, points structurés et/ou paragraphes, à côté d'une figure.
	// =====================================================================
	{
		type: ComponentTypes.TextWithFigure,
		category: "pages-dediees",
		skeleton: { kind: "columns", spans: [8, 4] },
		title: "Texte avec figure",
		description: "Deux colonnes : une introduction, des points structurés et/ou des paragraphes, à côté d'une image (empilés sur mobile).",
		tags: ["text", "content", "image", "editorial"],
		defaultContent: {
			intro: "Introduction mise en exergue.",
			items: [
				{ lead: "Toucher.", text: "Description du premier point." }
			],
			paragraphs: [],
			imageSrc: "",
			imageAlt: "",
			imagePosition: "right"
		},
		fields: [
			{
				type: "textarea",
				label: "Introduction",
				path: ["content", "intro"],
				helper: "Courte accroche mise en exergue au-dessus des points. Laisser vide pour la masquer."
			},
			{
				type: "array",
				label: "Points structurés",
				itemLabel: "Point",
				itemType: "object",
				defaultItem: { lead: "", text: "" },
				path: ["content", "items"],
				itemSummaryKeys: ["lead", "text"],
				fields: [
					{ type: "text", label: "Mot d'attaque", path: ["lead"], placeholder: "Toucher.", helper: "Début de phrase en gras (ex. « Toucher. »)." },
					{ type: "textarea", label: "Texte", path: ["text"] }
				]
			},
			{
				type: "array",
				label: "Paragraphes",
				itemLabel: "Paragraphe",
				itemType: "text",
				defaultItem: "",
				path: ["content", "paragraphs"],
				itemEditor: "richParagraph",
				helper: "Paragraphes libres affichés sous les points structurés."
			},
			{ type: "image", label: "Figure", path: ["content", "imageSrc"], helper: "Image affichée à côté du texte." },
			{ type: "text", label: "Texte alternatif de la figure", path: ["content", "imageAlt"] },
			{
				type: "select",
				label: "Position de la figure",
				path: ["content", "imagePosition"],
				options: [
					{ label: "Droite", value: "right" },
					{ label: "Gauche", value: "left" }
				],
				helper: "Côté de l'image sur écran large. Par défaut : droite."
			}
		],
		previewComponents: withPreview(ComponentTypes.TextWithFigure, {
			intro: "Quelques gestes simples permettent de vérifier l'authenticité d'un billet.",
			items: [
				{ lead: "Toucher.", text: "Le papier fiduciaire est ferme et l'impression principale est en relief." },
				{ lead: "Regarder.", text: "Le filigrane et le fil de sécurité apparaissent par transparence." },
				{ lead: "Incliner.", text: "L'encre à couleur changeante varie selon l'angle d'observation." }
			],
			paragraphs: ["En cas de doute, présentez le billet à votre agence bancaire la plus proche."],
			imageSrc: DEFAULT_PRERENDER_IMAGE,
			imageAlt: "Illustration du billet",
			imagePosition: "right"
		})
	},
	// =====================================================================
	// GovernorList : grille de cartes-portraits de tous les gouverneurs de
	// la BCC (ordinal, période de mandat, court résumé, lien vers la page
	// de détail). Le rendu trie automatiquement par ordinal décroissant.
	// =====================================================================
	{
		type: ComponentTypes.GovernorList,
		category: "pages-dediees",
		hidden: true,
		skeleton: { kind: "cards", count: 4, withImage: true },
		title: "Liste des gouverneurs",
		description: "Grille de cartes-portraits des gouverneurs : ordinal, période de mandat, court résumé et lien vers la page de détail.",
		tags: ["grid", "people", "card"],
		defaultContent: {
			title: "Les gouverneurs de la Banque Centrale du Congo",
			items: [
				{
					title: "Nom du gouverneur",
					ordre: "1",
					dateDebut: "1997-01-01",
					dateFin: "2013-12-31",
					image: "",
					summary: "Court résumé du mandat.",
					href: { slug: "" }
				}
			]
		},
		fields: [
			{
				type: "text",
				label: "Titre",
				path: ["content", "title"],
				helper: "Titre affiché au-dessus de la grille. Laisser vide pour le masquer."
			},
			{
				type: "array",
				label: "Gouverneurs",
				itemLabel: "Gouverneur",
				itemType: "object",
				defaultItem: { title: "", ordre: "", dateDebut: "", dateFin: "", image: "", summary: "", href: { slug: "" } },
				path: ["content", "items"],
				itemSummaryKeys: ["title", "summary"],
				fields: [
					{ type: "text", label: "Nom complet", path: ["title"] },
					{
						type: "text",
						label: "Ordinal",
						path: ["ordre"],
						placeholder: "15",
						helper: "Numéro d'ordre du mandat (« 0 » pour le Comité de gestion intérimaire). La grille est triée automatiquement par ordinal décroissant."
					},
					{ type: "text", label: "Début du mandat", path: ["dateDebut"], placeholder: "AAAA-MM-JJ" },
					{ type: "text", label: "Fin du mandat", path: ["dateFin"], placeholder: "AAAA-MM-JJ", helper: "Laisser vide pour un mandat en cours." },
					{ type: "image", label: "Portrait", path: ["image"], helper: "Laisser vide pour une carte sans photo." },
					{ type: "textarea", label: "Résumé", path: ["summary"], helper: "Courte phrase affichée sous le nom." },
					{
						type: "href",
						label: "Page de détail",
						path: ["href"],
						helper: "Page biographique du gouverneur."
					},
					{
						type: "text",
						label: "Identifiant de nœud",
						path: ["nodeId"],
						helper: "Champ technique (identifiant hérité de l'ancien site) : laisser vide pour un nouveau contenu."
					}
				]
			}
		],
		previewComponents: withPreview(ComponentTypes.GovernorList, {
			title: "Les gouverneurs de la Banque Centrale du Congo",
			items: [
				{
					title: "Premier gouverneur",
					ordre: "2",
					dateDebut: "2013-01-01",
					dateFin: "",
					image: DEFAULT_PRERENDER_IMAGE,
					summary: "Mandat en cours à la tête de l'institution.",
					href: { slug: "/la-banque/notre-gouverneur" }
				},
				{
					title: "Deuxième gouverneur",
					ordre: "1",
					dateDebut: "1997-01-01",
					dateFin: "2013-12-31",
					image: DEFAULT_PRERENDER_IMAGE,
					summary: "A conduit la réforme monétaire et la stabilisation du cadre macroéconomique.",
					href: { slug: "" }
				}
			]
		})
	},
	// =====================================================================
	// GovernorHero : en-tête de page de détail d'un gouverneur : portrait,
	// nom en H1 et lignes de méta (ordinal + période). Le sous-titre et la
	// période se déduisent de l'ordinal et des dates quand ils sont vides.
	// =====================================================================
	{
		type: ComponentTypes.GovernorHero,
		category: "pages-dediees",
		hidden: true,
		skeleton: { kind: "hero" },
		title: "En-tête gouverneur",
		description: "En-tête de page de détail : portrait, nom et lignes de méta (ordinal, période de mandat), déduites des dates quand elles sont vides.",
		tags: ["hero", "profile", "image"],
		defaultContent: {
			title: "Nom du gouverneur",
			subtitle: "",
			meta: "",
			ordre: "1",
			dateDebut: "1997-01-01",
			dateFin: "2013-12-31",
			image: "",
			imageAlt: ""
		},
		fields: [
			{ type: "text", label: "Nom complet", path: ["content", "title"], helper: "Affiché en titre principal de la page." },
			{
				type: "text",
				label: "Sous-titre",
				path: ["content", "subtitle"],
				placeholder: "15ème Gouverneur",
				helper: "Ligne au-dessus des métadonnées. Laisser vide pour la déduire automatiquement de l'ordinal."
			},
			{
				type: "text",
				label: "Ligne de méta",
				path: ["content", "meta"],
				placeholder: "1997 - 2013",
				helper: "Généralement la période du mandat. Laisser vide pour la déduire automatiquement des dates."
			},
			{
				type: "text",
				label: "Ordinal",
				path: ["content", "ordre"],
				placeholder: "15",
				helper: "Numéro d'ordre du mandat, utilisé pour déduire le sous-titre quand il est vide."
			},
			{ type: "text", label: "Début du mandat", path: ["content", "dateDebut"], placeholder: "AAAA-MM-JJ" },
			{ type: "text", label: "Fin du mandat", path: ["content", "dateFin"], placeholder: "AAAA-MM-JJ", helper: "Laisser vide pour un mandat en cours." },
			{ type: "image", label: "Portrait", path: ["content", "image"], helper: "Laisser vide pour un en-tête sans photo." },
			{ type: "text", label: "Texte alternatif du portrait", path: ["content", "imageAlt"], helper: "Par défaut : le nom du gouverneur." }
		],
		previewComponents: withPreview(ComponentTypes.GovernorHero, {
			title: "Nom du gouverneur",
			ordre: "15",
			dateDebut: "1997-01-01",
			dateFin: "2013-12-31",
			image: DEFAULT_PRERENDER_IMAGE,
			imageAlt: "Portrait du gouverneur"
		})
	},
	// =====================================================================
	// ViceGovernorList : liste titrée des vice-gouverneurs d'un gouverneur
	// (nom + période + portrait facultatif). Rien ne s'affiche quand la
	// liste est vide.
	// =====================================================================
	{
		type: ComponentTypes.ViceGovernorList,
		category: "pages-dediees",
		hidden: true,
		skeleton: { kind: "list", rows: 3 },
		title: "Liste des vice-gouverneurs",
		description: "Liste titrée des vice-gouverneurs d'un gouverneur : nom, période de mandat et portrait facultatif. Masquée quand elle est vide.",
		tags: ["grid", "people", "card"],
		defaultContent: {
			title: "Vice-gouverneurs",
			items: [
				{ nom: "Nom du vice-gouverneur", dateDebut: "", dateFin: "", image: "" }
			]
		},
		fields: [
			{
				type: "text",
				label: "Titre",
				path: ["content", "title"],
				helper: "Laisser vide pour utiliser « Vice-gouverneurs »."
			},
			{
				type: "array",
				label: "Vice-gouverneurs",
				itemLabel: "Vice-gouverneur",
				itemType: "object",
				defaultItem: { nom: "", dateDebut: "", dateFin: "", image: "" },
				path: ["content", "items"],
				itemSummaryKeys: ["nom"],
				fields: [
					{ type: "text", label: "Nom complet", path: ["nom"] },
					{ type: "text", label: "Début du mandat", path: ["dateDebut"], placeholder: "1997", helper: "Année ou texte libre." },
					{ type: "text", label: "Fin du mandat", path: ["dateFin"], placeholder: "2013" },
					{ type: "image", label: "Portrait", path: ["image"], helper: "Laisser vide pour afficher les initiales." }
				]
			}
		],
		previewComponents: withPreview(ComponentTypes.ViceGovernorList, {
			title: "Vice-gouverneurs",
			items: [
				{ nom: "Premier vice-gouverneur", dateDebut: "2010", dateFin: "2018", image: DEFAULT_PRERENDER_IMAGE },
				{ nom: "Deuxième vice-gouverneur", dateDebut: "2018", dateFin: "", image: "" }
			]
		})
	},
	// =====================================================================
	// KeyIndicatorsBar : bandeau défilant des indicateurs économiques clés
	// (cours de change, taux directeur, inflation…). Sur les pages
	// publiées via le fragment « key-indicators-bar », les valeurs sont
	// remplacées automatiquement par les données à jour.
	// =====================================================================
	{
		type: ComponentTypes.KeyIndicatorsBar,
		category: "donnees",
		skeleton: { kind: "ticker" },
		title: "Barre d'indicateurs clés",
		description: "Bandeau défilant des indicateurs économiques clés (cours de change, taux directeur, inflation…), avec lien « Tous les taux ».",
		tags: ["home", "markets", "indicators", "dynamic"],
		defaultContent: {
			ariaLabel: "Indicateurs clés",
			indicators: [
				{ label: "USD/CDF", value: "2 300,00", unit: "", delta: "+0,25 %", trend: "up" },
				{ label: "Taux directeur", value: "25,0", unit: "%", delta: "", trend: "flat" }
			],
			viewAllLabel: "Tous les taux",
			viewAllHref: "/marche-des-changes/cours-de-change"
		},
		fields: [
			{
				type: "text",
				label: "Libellé d'accessibilité",
				path: ["content", "ariaLabel"],
				helper: "Description du bandeau pour les lecteurs d'écran. Par défaut : « Indicateurs clés »."
			},
			{
				type: "array",
				label: "Indicateurs",
				itemLabel: "Indicateur",
				itemType: "object",
				defaultItem: { label: "", value: "", unit: "", delta: "", trend: "flat" },
				path: ["content", "indicators"],
				itemSummaryKeys: ["label", "value"],
				helper: "Rempli automatiquement à la publication lorsque le bloc est alimenté par le fragment « key-indicators-bar » : les valeurs saisies ici sont indicatives.",
				fields: [
					{ type: "text", label: "Libellé", path: ["label"], placeholder: "USD/CDF" },
					{ type: "text", label: "Valeur", path: ["value"], placeholder: "2 300,00" },
					{ type: "text", label: "Unité", path: ["unit"], placeholder: "%" },
					{ type: "text", label: "Variation", path: ["delta"], placeholder: "+0,25 %" },
					{
						type: "select",
						label: "Tendance",
						path: ["trend"],
						options: [
							{ label: "Hausse", value: "up" },
							{ label: "Baisse", value: "down" },
							{ label: "Stable", value: "flat" }
						],
						helper: "Détermine l'icône et la couleur de la variation."
					}
				]
			},
			{
				type: "text",
				label: "Libellé « voir tout »",
				path: ["content", "viewAllLabel"],
				helper: "Par défaut : « Tous les taux »."
			},
			{
				type: "text",
				label: "Cible « voir tout »",
				path: ["content", "viewAllHref"],
				placeholder: "/marche-des-changes/cours-de-change",
				helper: "Chemin de la page listant tous les taux."
			}
		],
		previewComponents: withPreview(ComponentTypes.KeyIndicatorsBar, {
			ariaLabel: "Indicateurs clés",
			indicators: [
				{ label: "USD/CDF", value: "2 301,64", delta: "+0,12 %", trend: "up" },
				{ label: "EUR/CDF", value: "2 512,08", delta: "-0,05 %", trend: "down" },
				{ label: "Taux directeur", value: "25,0", unit: "%", trend: "flat" },
				{ label: "Inflation (annuelle)", value: "9,8", unit: "%", delta: "-0,3 pt", trend: "down" }
			],
			viewAllLabel: "Tous les taux",
			viewAllHref: "/marche-des-changes/cours-de-change"
		})
	},
	// =====================================================================
	// InformationHub : bloc « publications + communiqués » à deux colonnes
	// (onglets de publications à gauche, communiqués de presse à droite,
	// rangée « Dernières vidéos » facultative). Les listes sont alimentées
	// automatiquement par le fragment « information-hub » à la publication.
	// =====================================================================
	{
		type: ComponentTypes.InformationHub,
		category: "accueil",
		skeleton: { kind: "columns", spans: [6, 6] },
		title: "Pôle d'information (publications + communiqués)",
		description: "Deux colonnes : onglets de publications à gauche, communiqués de presse à droite, avec une rangée « Dernières vidéos » facultative.",
		tags: ["home", "content", "dynamic"],
		defaultContent: {
			title: "Centre d'information",
			publicationsTitle: "",
			communiquesTitle: "Communiqués de presse",
			tabs: [
				{
					id: "publications",
					label: "Publications",
					viewAllLabel: "Toutes les publications",
					viewAllHref: { slug: "/publications" },
					items: []
				}
			],
			communiques: [],
			ctaLabel: "Toutes les publications",
			ctaHref: { slug: "/publications" },
			communiquesViewAllLabel: "Tous les communiqués",
			communiquesViewAllHref: { slug: "/actualites/communiques" },
			backgroundImageSrc: ""
		},
		fields: [
			{
				type: "text",
				label: "Titre de la section",
				path: ["content", "title"],
				placeholder: "Centre d'information",
				helper: "Laisser vide pour masquer le titre."
			},
			{
				type: "text",
				label: "Sur-titre des publications",
				path: ["content", "publicationsTitle"],
				placeholder: "Publications récentes",
				helper: "Petit libellé au-dessus de la carte des publications. Facultatif."
			},
			{
				type: "text",
				label: "Titre des communiqués",
				path: ["content", "communiquesTitle"],
				helper: "Par défaut : « Communiqués de presse »."
			},
			{
				type: "array",
				label: "Onglets de publications",
				itemLabel: "Onglet",
				itemType: "object",
				defaultItem: { id: "", label: "", viewAllLabel: "", viewAllHref: { slug: "" }, items: [] },
				path: ["content", "tabs"],
				itemSummaryKeys: ["label"],
				helper: "Les entrées de chaque onglet (et la liste des communiqués) sont alimentées automatiquement à la publication par le fragment « information-hub » : seuls les libellés se règlent ici.",
				fields: [
					{
						type: "text",
						label: "Identifiant",
						path: ["id"],
						placeholder: "publications",
						helper: "Champ technique : identifiant unique de l'onglet."
					},
					{ type: "text", label: "Libellé", path: ["label"], placeholder: "Publications" },
					{ type: "text", label: "Libellé « voir tout »", path: ["viewAllLabel"], placeholder: "Toutes les publications" },
					{
						type: "href",
						label: "Cible « voir tout »",
						path: ["viewAllHref"],
						helper: "Page listant tout le contenu de l'onglet."
					}
				]
			},
			{ type: "text", label: "Libellé du bouton bas de section", path: ["content", "ctaLabel"], placeholder: "Toutes les publications" },
			{
				type: "href",
				label: "Cible du bouton bas de section",
				path: ["content", "ctaHref"]
			},
			{
				type: "text",
				label: "Libellé « tous les communiqués »",
				path: ["content", "communiquesViewAllLabel"],
				placeholder: "Tous les communiqués"
			},
			{
				type: "href",
				label: "Cible « tous les communiqués »",
				path: ["content", "communiquesViewAllHref"]
			},
			{
				type: "image",
				label: "Image de fond",
				path: ["content", "backgroundImageSrc"],
				helper: "Filigrane décoratif estompé derrière la section. Facultatif."
			},
			{
				type: "number",
				label: "Dernières vidéos : nombre maximal",
				path: ["content", "latestVideos", "maxItems"],
				helper: "Les cartes vidéo sont remplies automatiquement depuis le fragment « latest-videos » ; ce champ plafonne leur nombre (3 maximum affichées)."
			},
			{
				type: "href",
				label: "Dernières vidéos : cible « voir tout »",
				path: ["content", "latestVideos", "viewAllHref"],
				helper: "Page listant toutes les vidéos ; le libellé du lien est traduit automatiquement."
			}
		],
		previewComponents: withPreview(ComponentTypes.InformationHub, {
			title: "Centre d'information",
			communiquesTitle: "Communiqués de presse",
			tabs: [
				{
					id: "publications",
					label: "Publications",
					viewAllLabel: "Toutes les notes de conjoncture",
					viewAllHref: { slug: "/publications/notes-de-conjoncture" },
					items: [
						{ type: "Notes de conjoncture", date: "Mercredi, 29 avril 2026", title: "Note de conjoncture : avril 2026", href: { slug: "" } },
						{ type: "Notes de conjoncture", date: "Mardi, 31 mars 2026", title: "Note de conjoncture : mars 2026", href: { slug: "" } },
						{ type: "Rapports annuels", date: "Lundi, 2 février 2026", title: "Rapport annuel 2025", href: { slug: "" } }
					]
				},
				{
					id: "statistiques",
					label: "Statistiques",
					viewAllLabel: "Toutes les statistiques",
					viewAllHref: { slug: "/statistiques" },
					items: [
						{ type: "Secteur réel", date: "Vendredi, 24 avril 2026", title: "Statistiques hebdomadaires du secteur réel", href: { slug: "" } }
					]
				}
			],
			communiques: [
				{ title: "Communiqué du Comité de politique monétaire", date: "Jeudi, 9 avril 2026", time: "18:30", badgeDay: "09", badgeMonth: "avr, 26", href: { slug: "" } },
				{ title: "Résultats de l'adjudication des Bons BCC", date: "Mardi, 7 avril 2026", badgeDay: "07", badgeMonth: "avr, 26", href: { slug: "" } }
			],
			ctaLabel: "Toutes les publications",
			ctaHref: { slug: "/publications" },
			communiquesViewAllLabel: "Tous les communiqués",
			communiquesViewAllHref: { slug: "/actualites/communiques" }
		})
	}
];

export const componentDefinitionMap = new Map(componentLibrary.map((definition) => [definition.type, definition]));
